/* eslint-disable react/prop-types */
"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Linkify from "linkify-react";
import { find as findLinks } from "linkifyjs";
import { useNavigate } from "react-router-dom";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import api from "../../api/axios";
import { isSupportCode } from "../../utils/supportIdentity";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { normalizeMessageReactions } from "../../utils/chatReactions";
import {
  formatChatLocationDisplayAddress,
  getChatLocationPayload,
  getChatPollPayload,
  isChatPollPayload,
} from "../../utils/chatStructuredPayloads";
import { toApiAbsoluteUrl } from "../../utils/ws";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import DirectChatEventContextNotice from "./DirectChatEventContextNotice";
import {
  DELETED_USER_LABEL,
  formatDeletedAccountText,
  formatUserHandle,
  isDeletedAccountUser,
  isDeletedUsername,
} from "../../utils/deletedAccount";
import AppModalShell from "../common/AppModalShell";
import { isNativeApp, openExternalUrl, toPublicWebUrl } from "../../native/runtime";
import { getNativeInternalPath } from "../../native/appLinkRouting";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { getChatStickerFromPayload } from "../../constants/chatStickers";
import { BADGES } from "../../constants/badges";
import { ReactComponent as DirectDeliveryCheckIcon } from "../../assets/icons/direct_delivery_check.svg";

const EMPTY_ATTACHMENTS = [];

const ITEM_TYPES = {
  MESSAGE: "message",
  SYSTEM: "system",
  SYSTEM_AS_USER: "system_as_user",
  DAY: "day_separator",
};

const CHAT_LINK_CLASS_BY_TONE = {
  self: "underline underline-offset-2 decoration-[#1D4ED8]/45 text-[#1D4ED8] hover:text-[#143E99] break-all font-semibold",
  other: "underline underline-offset-2 decoration-white/60 text-white hover:text-white break-all font-semibold",
  system: "underline underline-offset-2 decoration-white/45 text-blue-100 hover:text-white break-all font-semibold",
};

const CHAT_MENTION_CLASS_BY_TONE = {
  self: "text-[#1D4ED8] hover:text-[#143E99] font-semibold",
  other: "text-white hover:text-white font-extrabold",
  system: "text-blue-100 hover:text-white font-extrabold",
};

const CHAT_SELF_MESSAGE_BUBBLE = "#FFFFFF";
const CHAT_OTHER_MESSAGE_BUBBLE = "#7498FF";
const CHAT_LOCATION_MAP_STYLE_URL =
  "https://api.maptiler.com/maps/0196259d-93a4-75bb-af54-6b003b7377ab/style.json?key=QMa6CZX7KdzCIRISMOjh";
const PROTECTED_CHAT_ATTACHMENT_URL_RE = /\/api\/chats\/attachments\/\d+\/file\/?(?:[?#].*)?$/i;
const CHAT_EVENT_DETAILS_CACHE = new Map();
const CHAT_EVENT_DETAILS_REQUESTS = new Map();
const CHAT_CLUB_DETAILS_CACHE = new Map();
const CHAT_CLUB_DETAILS_REQUESTS = new Map();

export function parseChatEventLink(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue || /\s/.test(rawValue)) return null;

  const valueWithProtocol = /^vaultgrid\.net\//i.test(rawValue)
    ? `https://${rawValue}`
    : rawValue;
  const internalPath = getNativeInternalPath(valueWithProtocol);
  if (!internalPath) return null;

  let url;
  try {
    url = new URL(internalPath, "https://vaultgrid.net");
  } catch {
    return null;
  }

  const directEventMatch = url.pathname.match(/^\/events\/(\d+)(?:-[^/]+)?\/?$/i);
  const searchEventId = ["/events", "/events/", "/events/search", "/events/map_search"].includes(
    url.pathname
  )
    ? Number(url.searchParams.get("event") || 0)
    : 0;
  const eventId = Number(directEventMatch?.[1] || searchEventId || 0);
  if (!Number.isFinite(eventId) || eventId <= 0) return null;

  return {
    eventId,
    target: `${url.pathname}${url.search}${url.hash}`,
    originalUrl: rawValue,
  };
}

export function parseChatClubLink(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue || /\s/.test(rawValue)) return null;

  const valueWithProtocol = /^vaultgrid\.net\//i.test(rawValue)
    ? `https://${rawValue}`
    : rawValue;
  const internalPath = getNativeInternalPath(valueWithProtocol);
  if (!internalPath) return null;

  let url;
  try {
    url = new URL(internalPath, "https://vaultgrid.net");
  } catch {
    return null;
  }

  const clubMatch = url.pathname.match(/^\/clubs\/(\d+)(?:-[^/]+)?\/?$/i);
  const clubId = Number(clubMatch?.[1] || 0);
  if (!Number.isFinite(clubId) || clubId <= 0) return null;

  return {
    clubId,
    target: `${url.pathname}${url.search}${url.hash}`,
    originalUrl: rawValue,
  };
}

// Keep URL ranges so only the previewed link is hidden, never the surrounding copy.
export function findChatCardLink(value, parseLink) {
  const text = String(value || "");
  const standalone = parseLink(text);
  if (standalone) {
    const start = text.indexOf(standalone.originalUrl);
    return { ...standalone, start, end: start + standalone.originalUrl.length };
  }
  const candidates = findLinks(text).filter((link) => link.type === "url");
  // Relative app routes are also supported when pasted into a message.
  for (const match of text.matchAll(/(?:^|\s)(\/(?:events|clubs)\/[^\s<>]+)/g)) {
    const value = match[1].replace(/[.,!?;:]+$/, "");
    const start = match.index + match[0].indexOf(match[1]);
    candidates.push({ value, start, end: start + value.length });
  }
  for (const candidate of candidates.sort((a, b) => a.start - b.start)) {
    const link = parseLink(candidate.value);
    if (link) return { ...link, start: candidate.start, end: candidate.end };
  }
  return null;
}

function normalizeChatEventDetails(payload) {
  if (!payload || typeof payload !== "object") return null;
  const event = payload?.event && typeof payload.event === "object" ? payload.event : payload;
  if (!event || typeof event !== "object") return null;
  return event;
}

function loadChatEventDetails(eventId) {
  const cacheKey = String(eventId);
  if (CHAT_EVENT_DETAILS_CACHE.has(cacheKey)) {
    return Promise.resolve(CHAT_EVENT_DETAILS_CACHE.get(cacheKey));
  }
  if (CHAT_EVENT_DETAILS_REQUESTS.has(cacheKey)) {
    return CHAT_EVENT_DETAILS_REQUESTS.get(cacheKey);
  }

  const request = api
    .get(`/events/details/${encodeURIComponent(cacheKey)}/`, {
      authOptional: true,
      suppressUnauthorizedRedirect: true,
      meta: { suppressGlobalError: true },
    })
    .then((response) => {
      const event = normalizeChatEventDetails(response?.data);
      if (!event) throw new Error("event_details_unavailable");
      CHAT_EVENT_DETAILS_CACHE.set(cacheKey, event);
      return event;
    })
    .finally(() => {
      CHAT_EVENT_DETAILS_REQUESTS.delete(cacheKey);
    });

  CHAT_EVENT_DETAILS_REQUESTS.set(cacheKey, request);
  return request;
}

function loadChatClubDetails(clubId) {
  const cacheKey = String(clubId);
  if (CHAT_CLUB_DETAILS_CACHE.has(cacheKey)) {
    return Promise.resolve(CHAT_CLUB_DETAILS_CACHE.get(cacheKey));
  }
  if (CHAT_CLUB_DETAILS_REQUESTS.has(cacheKey)) {
    return CHAT_CLUB_DETAILS_REQUESTS.get(cacheKey);
  }

  const request = api
    .get(`/clubs/details/${encodeURIComponent(cacheKey)}/`, {
      authOptional: true,
      suppressUnauthorizedRedirect: true,
      meta: { suppressGlobalError: true },
    })
    .then((response) => {
      const club =
        response?.data?.club && typeof response.data.club === "object"
          ? response.data.club
          : response?.data;
      if (!club || typeof club !== "object") throw new Error("club_details_unavailable");
      CHAT_CLUB_DETAILS_CACHE.set(cacheKey, club);
      return club;
    })
    .finally(() => {
      CHAT_CLUB_DETAILS_REQUESTS.delete(cacheKey);
    });

  CHAT_CLUB_DETAILS_REQUESTS.set(cacheKey, request);
  return request;
}

function resolveChatEventImageSrc(event) {
  const badgeSrc = BADGES.find((badge) => badge.id === event?.badge_label)?.src || "";
  return String(
    event?.hero_image_url ||
      event?.cover_image_url ||
      event?.cover_url ||
      event?.image_url ||
      event?.icon_image_url ||
      badgeSrc ||
      ""
  ).trim() || null;
}

function resolveChatClubImageSrc(club) {
  return String(
    club?.hero_image_url ||
      club?.image_url ||
      club?.avatar_custom_url ||
      club?.avatar_url ||
      ""
  ).trim() || null;
}

function formatChatEventDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale || undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function DirectDeliveryChecks({ read = false, className = "" }) {
  if (!read) {
    return (
      <DirectDeliveryCheckIcon
        className={className}
        data-testid="direct-delivered-receipt"
        aria-hidden="true"
      />
    );
  }

  return (
    <svg
      width="18"
      height="11"
      viewBox="0 0 20 11"
      fill="none"
      className={className}
      data-testid="direct-read-receipt"
      aria-hidden="true"
    >
      <path
        d="M6 5.24268L10.24264 9.48532L18.727 1M1 5.24268L5.24264 9.48532M13.727 1L10.477 4.25"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PendingDeliveryIndicator({ className = "" }) {
  return (
    <span
      className={`inline-block h-[10px] w-[10px] animate-spin rounded-full border-[1.5px] border-current border-t-transparent ${className}`}
      data-testid="chat-pending-delivery"
      aria-hidden="true"
    />
  );
}

function getDirectPhotoConsentPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (String(payload?.kind || "").trim().toLowerCase() !== "direct_photo_consent") {
    return null;
  }
  return payload;
}

function DirectPhotoConsentIcon({ accepted = false, className = "" }) {
  return (
    <span
      className={`relative flex shrink-0 items-center justify-center rounded-[13px] border ${
        accepted
          ? "border-[#9CC0FF]/35 bg-[#5F8EFF]/22 text-[#DDE9FF]"
          : "border-white/15 bg-white/10 text-white/90"
      } ${className}`}
      aria-hidden="true"
    >
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
        <path
          d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5v-9Z"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="m6.5 16 3.4-3.6 2.4 2.4 2.2-2.2 3 3.4M16.5 9.2h.01"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {accepted ? (
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#202A40] bg-[#78A0FF] text-white">
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path
              d="m1 3.5 2.15 2L8 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : null}
    </span>
  );
}

function DirectPhotoConsentMessage({
  item,
  payload,
  currentUserId = null,
  submitting = false,
  onAction,
  t,
}) {
  const requestedById = Number(payload?.requested_by_id || 0);
  const viewerId = Number(currentUserId || 0);
  const isSelf = requestedById > 0 && viewerId > 0
    ? requestedById === viewerId
    : Boolean(item?.is_self);
  const status = String(payload?.status || "pending").toLowerCase();
  const canRespond = !isSelf && (status === "pending" || status === "declined");
  const isAccepted = status === "accepted";
  const title = status === "accepted"
    ? t("chats.direct_chat.photo_consent.message_allowed")
    : t("chats.direct_chat.photo_consent.message_title");
  const description = status === "accepted"
    ? t("chats.direct_chat.photo_consent.message_allowed_body")
    : isSelf
      ? status === "declined"
        ? t("chats.direct_chat.photo_consent.message_declined")
        : t("chats.direct_chat.photo_consent.message_requested")
      : status === "declined"
        ? t("chats.direct_chat.photo_consent.message_declined_change")
        : t("chats.direct_chat.photo_consent.message_incoming");

  if (!canRespond) {
    return (
      <div data-msg-id={item?.id} className="my-2.5 flex w-full justify-center px-2">
        <div
          data-testid="direct-photo-consent-card"
          className={`w-fit max-w-[380px] rounded-[18px] border px-3.5 py-3 text-white shadow-[0_8px_22px_rgba(0,0,0,0.12)] ${
            isAccepted
              ? "border-[#8DB4FF]/30 bg-[#253451]/90"
              : "border-white/15 bg-white/[0.08]"
          }`}
        >
          <div className="flex items-center gap-3">
            <DirectPhotoConsentIcon
              accepted={isAccepted}
              className="h-9 w-9"
            />
            <div className="min-w-0 flex-1">
              {isAccepted ? (
                <>
                  <p className="text-left text-[13px] font-bold leading-[18px] text-white">
                    {title}
                  </p>
                  <p
                    data-testid="direct-photo-consent-copy"
                    className="mt-0.5 text-pretty text-left text-[13px] font-medium leading-[18px] text-white/72"
                  >
                    {description}
                  </p>
                </>
              ) : (
                <p
                  data-testid="direct-photo-consent-copy"
                  className="text-pretty text-left text-[14px] font-medium leading-[20px] text-white/90"
                >
                  {description}
                </p>
              )}
              <div
                className="mt-1.5 flex items-center justify-end gap-1 text-[11px] leading-none text-white/50"
                data-testid="direct-photo-consent-time"
              >
                {isSelf ? (
                  <DirectDeliveryChecks
                    read={Boolean(item?.read_by_peer)}
                    className="shrink-0"
                  />
                ) : null}
                <span>{item?.time || ""}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-msg-id={item?.id}
      className="my-3 flex w-full justify-center px-2"
    >
      <div
        data-testid="direct-photo-consent-card"
        className="w-full max-w-[380px] rounded-[20px] border border-white/16 bg-[#202A40]/95 p-4 text-white shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
      >
        <div className="flex items-center gap-3">
          <DirectPhotoConsentIcon className="h-10 w-10" />
          <div className="min-w-0 flex-1 text-left">
            <p className="text-[14px] font-bold leading-5 text-white">{title}</p>
            <p className="mt-0.5 text-pretty text-[13px] font-medium leading-[18px] text-white/68">
              {description}
            </p>
          </div>
        </div>
        <div
          className={`mt-4 grid gap-2 ${status === "pending" ? "grid-cols-2" : "grid-cols-1"}`}
        >
          <button
            type="button"
            disabled={submitting}
            onClick={() => onAction?.("accept", payload?.request_id)}
            className="h-10 rounded-[12px] bg-[#729BFF] px-3 text-[13px] font-semibold text-white shadow-[0_5px_14px_rgba(65,111,220,0.28)] transition active:scale-[0.98] disabled:opacity-50"
          >
            {t("chats.direct_chat.photo_consent.allow")}
          </button>
          {status === "pending" ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => onAction?.("decline", payload?.request_id)}
              className="h-10 rounded-[12px] border border-white/14 bg-white/[0.07] px-3 text-[13px] font-semibold text-white/86 transition active:scale-[0.98] disabled:opacity-50"
            >
              {t("chats.direct_chat.photo_consent.not_now")}
            </button>
          ) : null}
        </div>
        <div
          className="mt-2.5 flex items-center justify-end gap-1 text-[11px] leading-none text-white/45"
          data-testid="direct-photo-consent-time"
        >
          {isSelf ? <DirectDeliveryChecks read={Boolean(item?.read_by_peer)} className="shrink-0" /> : null}
          <span>{item?.time || ""}</span>
        </div>
      </div>
    </div>
  );
}

const SYSTEM_BUTTON_TRANSLATION_KEYS = {
  "events.post_event.button_gallery": "system_buttons.post_event.gallery",
  "events.post_event.button_instagram": "system_buttons.post_event.instagram",
  "events.post_event.button_review": "system_buttons.post_event.review",
  "events.gallery.uploaded.button": "system_buttons.gallery_upload.open_gallery",
};

function getLegacySystemButtonI18nKey(payload, button, index) {
  const systemI18nKey = String(payload?.system_i18n_key || "").trim();
  if (systemI18nKey === "events.post_event.message") {
    return [
      "events.post_event.button_gallery",
      "events.post_event.button_instagram",
      "events.post_event.button_review",
    ][index] || null;
  }

  const isGalleryUpload = String(payload?.kind || "").trim() === "event_gallery_upload";
  const target = String(button?.to || "").trim();
  if (isGalleryUpload && /\/events\/\d+\/gallery\/?$/i.test(target)) {
    return "events.gallery.uploaded.button";
  }

  return null;
}

function getLocalizedSystemButtonLabel(payload, button, index, t) {
  const fallback = String(button?.label || "").trim();
  const rawI18nKey = String(
    button?.i18n_key || getLegacySystemButtonI18nKey(payload, button, index) || ""
  ).trim();
  const frontendI18nKey = SYSTEM_BUTTON_TRANSLATION_KEYS[rawI18nKey];
  if (!frontendI18nKey) return fallback;

  return t(`events:${frontendI18nKey}`, { defaultValue: fallback });
}

function getEventGalleryUploadPreview(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (String(payload?.kind || "").trim() !== "event_gallery_upload") return null;

  const eventId = Number(payload?.event_id || 0);
  if (!Number.isInteger(eventId) || eventId <= 0) return null;

  const previewMedia = (Array.isArray(payload?.preview_media) ? payload.preview_media : [])
    .map((media) => ({
      id: Number(media?.id || 0),
      type: String(media?.type || "").trim().toLowerCase() === "video" ? "video" : "image",
    }))
    .filter((media) => Number.isInteger(media.id) && media.id > 0)
    .slice(0, 3);
  if (!previewMedia.length) return null;

  const galleryButton = (Array.isArray(payload?.buttons) ? payload.buttons : []).find((button) =>
    /\/events\/\d+\/gallery\/?$/i.test(String(button?.to || "").trim())
  );

  return {
    eventId,
    previewMedia,
    uploadedCount: Math.max(previewMedia.length, Number(payload?.uploaded_count || 0)),
    target: String(galleryButton?.to || `/events/${eventId}/gallery`).trim(),
  };
}

function getVideoPreviewSource(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url || url.includes("#")) return url;
  return `${url}#t=0.1`;
}

function useRenderableChatImageUrls(imageUrls) {
  const [nativeUrls, setNativeUrls] = useState([]);
  const immediateNativeUrls = useMemo(
    () =>
      (Array.isArray(imageUrls) ? imageUrls : []).filter(
        (url) => !PROTECTED_CHAT_ATTACHMENT_URL_RE.test(String(url || "").trim())
      ),
    [imageUrls]
  );

  useEffect(() => {
    if (!isNativeApp() || !imageUrls.length) {
      setNativeUrls((current) => (current.length ? [] : current));
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];
    const sourceUrls = Array.isArray(imageUrls) ? imageUrls : [];

    setNativeUrls([]);

    Promise.all(
      sourceUrls.map(async (rawUrl) => {
        const sourceUrl = String(rawUrl || "").trim();
        if (!sourceUrl) return null;
        if (!PROTECTED_CHAT_ATTACHMENT_URL_RE.test(sourceUrl)) return sourceUrl;

        try {
          const response = await api.get(toApiAbsoluteUrl(sourceUrl) || sourceUrl, {
            responseType: "blob",
            timeout: 30000,
            meta: { suppressGlobalError: true },
          });
          const objectUrl = URL.createObjectURL(response.data);
          objectUrls.push(objectUrl);
          return objectUrl;
        } catch {
          // Do not fall back to a protected HTTP URL: native <img> elements
          // cannot include the required Bearer token.
          return null;
        }
      })
    ).then((nextUrls) => {
      if (!cancelled) setNativeUrls(nextUrls.filter(Boolean));
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  return isNativeApp()
    ? (nativeUrls.length ? nativeUrls : immediateNativeUrls)
    : imageUrls;
}

// Legacy accounts may still contain dots, so chat mentions must stay clickable
// even though new usernames are validated more strictly on the backend.
const USERNAME_RE = /@[\p{L}\p{N}_][\p{L}\p{N}._]{1,31}/gu;

const EMOJI_ONLY_RE =
  /^(?:\p{Extended_Pictographic}|[\u200D\uFE0F]|\s)+$/u;

const EMOJI_RE = /\p{Extended_Pictographic}/gu;
const CHAT_TRANSLATION_LANGS = new Set(["en", "ru", "sk"]);
const SYSTEM_ACTION_TRAILING_URL_RE =
  /\s*((?:https?:\/\/(?:www\.)?vaultgrid\.net)?\/[A-Za-z0-9][^\s]*)\s*$/i;

function normalizeChatTranslationLang(raw) {
  const value = String(raw || "").trim().toLowerCase().split("-")[0];
  return CHAT_TRANSLATION_LANGS.has(value) ? value : "en";
}

function normalizeSystemMessageLineBreaks(raw) {
  return String(raw || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n");
}

function showTranslationError(message) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-error", {
      detail: {
        message:
          message || "Translation is temporarily unavailable. Please try again.",
        status: 503,
      },
    })
  );
}

function ChatLocationPreviewMap({ lat, lng, authorLabel = "User", className = "h-[132px]" }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLabel = String(authorLabel || "User").slice(0, 18);

  const center = useMemo(() => {
    const nextLat = Number(lat);
    const nextLng = Number(lng);
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return null;
    return { lat: nextLat, lng: nextLng };
  }, [lat, lng]);

  useEffect(() => {
    if (!center || !mapContainerRef.current) return undefined;

    let map = null;
    let resizeTimer = null;

    try {
      map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: CHAT_LOCATION_MAP_STYLE_URL,
        center: [center.lng, center.lat],
        zoom: 15,
        attributionControl: false,
        interactive: false,
      });
      mapRef.current = map;
      map.on("load", () => {
        map.resize();
      });
      resizeTimer = window.setTimeout(() => map?.resize?.(), 0);
    } catch {
      // Keep the card usable if the map provider is unavailable.
    }

    return () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      try {
        map?.remove?.();
      } catch {
        // ignore
      }
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [center]);

  return (
    <div className={`relative overflow-hidden bg-[#1E345D] ${className}`}>
      <div ref={mapContainerRef} className="pointer-events-none absolute inset-0" aria-label="Location map preview" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,16,32,0.02)_0%,rgba(8,16,32,0.18)_100%)]" />
      <div
        className="vg-chat-location-marker vg-chat-location-marker--active pointer-events-none absolute left-1/2 top-1/2"
        style={{ transform: "translate(-50%, calc(-100% + 7px))" }}
        aria-hidden="true"
      >
        <span>{markerLabel}</span>
      </div>
    </div>
  );
}

function extractTranslationPayload(
  payload,
  { normalizeEscapedLineBreaks = false } = {}
) {
  const raw = payload?.translations;
  if (!raw || typeof raw !== "object") return null;

  const map = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === "base_lang") continue;
    if (typeof value !== "string") continue;
    const normalizedKey = normalizeChatTranslationLang(key);
    // Some legacy system translations contain the two visible characters
    // `\\n`. Keep user-authored chat translations unchanged.
    const cleanValue = (
      normalizeEscapedLineBreaks
        ? normalizeSystemMessageLineBreaks(value)
        : String(value || "")
    ).trim();
    if (!cleanValue) continue;
    map[normalizedKey] = cleanValue;
  }

  const keys = Object.keys(map);
  if (!keys.length) return null;

  const baseLang =
    typeof raw?.base_lang === "string"
      ? normalizeChatTranslationLang(raw.base_lang)
      : null;

  return { keys, map, baseLang };
}

function normalizeMentionUsername(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  return (s.startsWith("@") ? s.slice(1) : s).toLowerCase();
}

function isEligibleMention(s, start, end, allowedSet) {
  const prev = start > 0 ? s[start - 1] : "";
  const next = end < s.length ? s[end] : "";

  // Do not convert mentions embedded into e-mails/urls/words.
  if (prev && /[A-Za-z0-9._%+\-/]/.test(prev)) return false;
  if (next && /[\p{L}\p{N}._]/u.test(next)) return false;

  // Mentions are rendered eagerly; existence is validated on click.
  if (allowedSet instanceof Set && allowedSet.size > 0) {
    const uname = normalizeMentionUsername(s.slice(start, end));
    if (!allowedSet.has(uname)) return false;
  }

  return true;
}

function getChatLinkifyOptions(tone = "other") {
  return {
    attributes: {
      target: "_blank",
      rel: "noopener noreferrer",
      class: CHAT_LINK_CLASS_BY_TONE[tone] || CHAT_LINK_CLASS_BY_TONE.other,
    },
  };
}

function renderTextWithUsernameLinks(text, onUsernameClick, allowedSet, tone = "other") {
  const s = String(text || "");
  if (!s) return null;

  USERNAME_RE.lastIndex = 0;

  const nodes = [];
  let last = 0;
  let m;
  const linkifyOptions = getChatLinkifyOptions(tone);
  const mentionClassName = CHAT_MENTION_CLASS_BY_TONE[tone] || CHAT_MENTION_CLASS_BY_TONE.other;

  while ((m = USERNAME_RE.exec(s)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const mentionAllowed = isEligibleMention(s, start, end, allowedSet);

    if (!mentionAllowed) {
      continue;
    }

    if (start > last) {
      const chunk = s.slice(last, start);
      nodes.push(
        <Linkify key={`t-${last}`} options={linkifyOptions}>
          {chunk}
        </Linkify>
      );
    }

    const raw = m[0]; // includes leading @
    nodes.push(
      <span
        key={`u-${start}`}
        className={`${mentionClassName} cursor-pointer break-all`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUsernameClick?.(raw);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onPointerCancel={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onUsernameClick?.(raw);
          }
        }}
      >
        {raw}
      </span>
    );

    last = end;
  }

  if (last < s.length) {
    const chunk = s.slice(last);
    nodes.push(
      <Linkify key={`t-${last}`} options={linkifyOptions}>
        {chunk}
      </Linkify>
    );
  }

  return nodes;
}

function formatDayLabel(dateStr, locale = undefined) {
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr || "");

    return new Intl.DateTimeFormat(locale || undefined, {
      day: "2-digit",
      month: "long",
    }).format(d);
  } catch {
    return String(dateStr || "");
  }
}

function DaySeparator({ date, locale }) {
  return (
    <div className="flex items-center justify-center my-5">
      <div className="text-white text-[12px] font-black leading-[20px] tracking-wide">
        {formatDayLabel(date, locale)}
      </div>
    </div>
  );
}

function parsePollTimeToMinutes(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return hours * 60 + minutes;
}

function conceptPollOptionSortKey(option) {
  const rawDate = String(option?.date || "").trim();
  let dateRank = Number.MAX_SAFE_INTEGER;
  if (rawDate) {
    const dateObj = new Date(`${rawDate}T00:00:00`);
    if (!Number.isNaN(dateObj.getTime())) {
      dateRank = dateObj.getTime();
    }
  }

  const startRank = parsePollTimeToMinutes(option?.start_time);
  const endRank = parsePollTimeToMinutes(option?.end_time);

  return [
    dateRank,
    startRank == null ? -1 : startRank, // date-only first within same day
    endRank == null ? -1 : endRank,
    Number(option?.position || 0),
    Number(option?.id || 0),
  ];
}

function resolvePollVoterAvatarSrc(voter) {
  return resolveUserAvatarSrc(voter);
}

function ChatPollResultsModal({
  open,
  onClose,
  pollPayload,
  votesLabel,
  t,
}) {
  const options = Array.isArray(pollPayload?.options) ? pollPayload.options : [];
  const question = String(pollPayload?.title || "").trim();
  const closeLabel = t("chats.event_chat.poll.results_close", {
    defaultValue: "Close poll votes",
  });

  return (
    <AppModalShell
      open={open}
      title={t("chats.event_chat.poll.results_title", {
        defaultValue: "Poll votes",
      })}
      onClose={onClose}
      closeAriaLabel={closeLabel}
      zIndexClassName="z-[1200]"
      maxWidthClassName="max-w-[520px]"
      desktopMaxHeight="min(82vh, 680px)"
      titleClassName="text-[clamp(1.35rem,5.4vw,1.68rem)] sm:text-[28px]"
      bodyClassName="pt-0"
    >
      <div className="space-y-4">
        <div className="rounded-[20px] border border-white/14 bg-white/10 px-4 py-3">
          {question ? (
            <div className="break-words text-[18px] font-semibold leading-[23px] text-white">
              {question}
            </div>
          ) : null}
          {votesLabel ? (
            <div className="mt-1 text-[13px] font-semibold leading-[17px] text-white/62">
              {votesLabel}
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          {options.map((option) => {
            const voters = Array.isArray(option?.voters) ? option.voters : [];
            const voterIds = Array.isArray(option?.voter_ids) ? option.voter_ids : [];
            const count = voterIds.length || voters.length;
            return (
              <div
                key={`poll-results-${option.id}`}
                className="rounded-[18px] border border-white/12 bg-white/[0.07] px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 break-words text-[15px] font-semibold leading-[20px] text-white">
                    {option.text}
                  </div>
                  <div className="shrink-0 rounded-full bg-white/12 px-2.5 py-1 text-[12px] font-bold leading-none text-white/82">
                    {count}
                  </div>
                </div>

                {voters.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {voters.map((voter, index) => {
                      const avatarSrc = resolvePollVoterAvatarSrc(voter);
                      return (
                        <span
                          key={`poll-results-voter-${option.id}-${voter?.user_id || index}`}
                          className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-white/18 bg-white/12"
                          aria-label={t("chats.event_chat.poll.voter_avatar_aria", {
                            defaultValue: "Voter",
                          })}
                        >
                          {avatarSrc ? (
                            <img
                              src={avatarSrc}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </AppModalShell>
  );
}

function compareConceptPollOptions(a, b) {
  const left = conceptPollOptionSortKey(a);
  const right = conceptPollOptionSortKey(b);
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] < right[i]) return -1;
    if (left[i] > right[i]) return 1;
  }
  return 0;
}

export function resolvePayloadButtonTarget(to, kind) {
  const rawTo = String(to || "").trim();
  const normalizedKind = String(kind || "").trim().toLowerCase();
  if (!rawTo) return { type: "none", value: "" };

  if (rawTo.startsWith("/")) {
    const internalPath = getNativeInternalPath(rawTo);
    return internalPath
      ? { type: "internal", value: internalPath }
      : { type: "external", value: toPublicWebUrl(rawTo) };
  }

  if (/^https?:\/\//i.test(rawTo)) {
    const internalPath = getNativeInternalPath(rawTo);
    if (internalPath) return { type: "internal", value: internalPath };

    if (normalizedKind === "external") {
      return { type: "external", value: rawTo };
    }

    return { type: "external", value: toPublicWebUrl(rawTo) };
  }

  if (normalizedKind === "external") {
    return { type: "external", value: rawTo };
  }

  if (rawTo.startsWith("?") || rawTo.startsWith("#")) {
    return { type: "internal", value: rawTo };
  }
  const normalizedPath = `/${rawTo}`;
  const internalPath = getNativeInternalPath(normalizedPath);
  return internalPath
    ? { type: "internal", value: internalPath }
    : { type: "external", value: toPublicWebUrl(normalizedPath) };
}

function extractEventHostContactPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (String(payload?.kind || "").trim().toLowerCase() !== "event_host_contact_context") {
    return null;
  }

  const eventData =
    payload?.event && typeof payload.event === "object" ? payload.event : null;
  const eventId = Number(payload?.event_id || eventData?.id || 0);
  if (!eventId || !eventData) return null;

  return {
    event: {
      ...eventData,
      id: eventId,
    },
  };
}

function getPayloadEventLink(payload) {
  if (!payload || typeof payload !== "object") return null;
  const kind = String(payload?.kind || "").trim().toLowerCase();
  if (!["club_event_published", "admin_event_share", "new_joiners_event_published"].includes(kind)) {
    return null;
  }

  const eventPath = String(payload?.event_path || "").trim();
  const eventUrl = String(payload?.event_url || "").trim();
  const eventId = Number(payload?.event_id || 0);
  const to = eventPath || eventUrl || (eventId ? `/events/${eventId}` : "");
  if (!eventId || !to) return null;

  return {
    eventId,
    target: to,
    originalUrl: to,
  };
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripClubEventPublishedUrl(text, payload) {
  if (!payload || typeof payload !== "object") return text;
  if (!["club_event_published", "new_joiners_event_published"].includes(String(payload?.kind || "").trim().toLowerCase())) {
    return text;
  }

  let result = String(text || "");
  const eventUrl = String(payload?.event_url || "").trim();
  if (eventUrl) {
    result = result.replace(new RegExp(`\\s*${escapeRegExp(eventUrl)}\\s*$`, "i"), "");
  }

  result = result.replace(/\s*https?:\/\/(?:www\.)?vaultgrid\.net\/events\/\S+\s*$/i, "");
  return result.trimEnd();
}

function extractTrailingSystemActionUrl(text) {
  const rawText = String(text || "");
  const match = rawText.match(SYSTEM_ACTION_TRAILING_URL_RE);
  if (!match) return null;

  const url = String(match[1] || "").trim();
  if (!url) return null;

  return {
    url,
    text: rawText.slice(0, match.index).trimEnd(),
  };
}

function getSystemActionUrlButtonLabel(url, t) {
  const normalizedUrl = String(url || "").trim().toLowerCase();
  if (normalizedUrl.includes("activation_date=1")) {
    return t("mailbox.actions.open_date_confirmation", {
      defaultValue: "Open date confirmation",
    });
  }
  if (normalizedUrl.includes("confirm=1")) {
    return t("mailbox.actions.open_attendance_confirmation", {
      defaultValue: "Confirm arrival",
    });
  }
  if (normalizedUrl.includes("host_confirm=1")) {
    return t("mailbox.actions.open_host_confirmation", {
      defaultValue: "Open host confirmation",
    });
  }
  if (normalizedUrl.includes("pay=1")) {
    return t("mailbox.actions.open_payment", { defaultValue: "Open payment" });
  }
  if (normalizedUrl.includes("intent=1")) {
    return t("mailbox.actions.open_intention_confirmation", {
      defaultValue: "Confirm intention",
    });
  }
  return t("mailbox.actions.open_event", { defaultValue: "Open event" });
}

function SystemMessage({
  lines = [],
  payload = null,
  locale = "en",
  onUsernameClick,
  allowedMentionSet = null,
}) {
  const translatedLines = useMemo(() => {
    const targetLang = normalizeChatTranslationLang(locale);
    const translationPayload = extractTranslationPayload(payload, {
      normalizeEscapedLineBreaks: true,
    });

    if (translationPayload?.baseLang && translationPayload.baseLang === targetLang) {
      return lines;
    }

    const cached = translationPayload?.map?.[targetLang];
    if (cached) {
      return String(cached).split("\n");
    }

    return lines;
  }, [lines, locale, payload]);

  const displayLines = translatedLines.map((line) => formatDeletedAccountText(line));

  if (!displayLines.length) return null;

  return (
    <div className="flex flex-col items-start text-white text-[14px] font-extrabold leading-[20px] space-y-1.5 my-1 px-3">
      {displayLines.map((l, idx) => (
        <div
          key={idx}
          className="whitespace-pre-wrap break-words text-center w-full"
        >
          {renderTextWithUsernameLinks(l, onUsernameClick, allowedMentionSet, "system")}
        </div>
      ))}
    </div>
  );
}

function SenderBadge({
  code,
  avatarSrc = null,
  disabled = false,
  isPremium = false,
}) {
  const { openProfileCard } = useProfileCardModal();
  return (
    <div className="w-10 shrink-0 flex flex-col items-center justify-end">
      {code ? (
        <button
          type="button"
          disabled={disabled}
          aria-label={`Open ${code}'s profile`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openProfileCard({ code, avatar_url: avatarSrc });
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          className="disabled:cursor-default"
        >
          {isPremium ? (
          <PremiumCodeLabel
            code={code}
            isPremium
            className="mb-1.5 min-w-[38px] text-center text-white text-[12px] font-black"
          />
        ) : (
          <div className="text-white text-[13px] font-black text-center leading-none mb-1.5">
            {code}
          </div>
          )}
        </button>
      ) : null}

      {disabled ? (
        <div
          className="w-9 h-9 rounded-full bg-[#C98484] overflow-hidden flex items-center justify-center"
          aria-label="system-avatar"
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={code || ""}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          className="w-9 h-9 rounded-full bg-[#C98484] overflow-hidden flex items-center justify-center"
          aria-label="open-profile"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openProfileCard({ code, avatar_url: avatarSrc });
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onPointerCancel={(e) => e.stopPropagation()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={code || ""}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </button>
      )}
    </div>
  );
}

export function MessageBubble({
  item,
  onRequestMenu,
  onToggleReaction,
  onOpenImage,
  isContextHidden = false,
  disableInteractions = false,
  flashId = null,
  onJumpTo,
  avatarSrc = null,
  onOpenProfileByCode,
  hideSenderBadge = false,
  variant = "event",
  allowedMentionSet = null,
  pollEventId = null,
  currentUserId = null,
  selectionMode = false,
  isSelectableMessage = false,
  isSelectedMessage = false,
  onSelectMessage,
  onOpenLocation,
  onVotePoll,
  onOpenHostRules,
}) {
  const { i18n, t } = useTranslation(["menu", "events", "communities"]);
  const navigate = useNavigate();
  const {
    is_self,
    sender_username,
    sender_code,
    sender_has_premium_subscription,
    role,
    author_is_staff,
    text,
    payload,
    time,
    message_type,
    attachments = EMPTY_ATTACHMENTS,
    reactions = [],
    reply_to,
    reply_to_id,
    edited_at,
    read_by_peer,
    delivery_status,
  } = item || {};

  const isPendingDelivery = delivery_status === "sending";
  const interactionsDisabled = disableInteractions || isPendingDelivery;
  const showDirectReadReceipt = variant === "direct" && !!is_self;
  const isDirectMessageReadByPeer = showDirectReadReceipt && !!read_by_peer;
  const normalizedReactions = useMemo(
    () => normalizeMessageReactions(reactions, currentUserId),
    [reactions, currentUserId]
  );
  const canReact =
    typeof onToggleReaction === "function" &&
    !!item?.id &&
    !interactionsDisabled &&
    (message_type == null ||
      message_type === "text" ||
      message_type === "sticker" ||
      message_type === "system_as_user");

  const { isEmojiOnly, emojiCount } = useMemo(() => {
    const s = String(text || "").trim();
    if (!s) return { isEmojiOnly: false, emojiCount: 0 };

    if (!EMOJI_ONLY_RE.test(s)) {
      return { isEmojiOnly: false, emojiCount: 0 };
    }

    const count = (s.match(EMOJI_RE) || []).length;
    return { isEmojiOnly: true, emojiCount: count };
  }, [text]);

  // Normalize author id so ctx menus / Seen panels can reliably exclude the message author.
  // Backend fields can vary (author_id / sender_id / user_id / from_user_id / sender_user_id ...)
  const normalizedAuthorId =
    item?.author_id ??
    item?.sender_id ??
    item?.sender_user_id ??
    item?.user_id ??
    item?.from_user_id ??
    item?.author?.id ??
    item?.sender?.id ??
    null;

  // Ensure the object we pass to overlays always has author_id if it exists in any shape.
  const ctxItem =
    normalizedAuthorId != null && item && item.author_id == null
      ? { ...item, author_id: normalizedAuthorId }
      : item;
  const canOpenReactionDetails =
    typeof onRequestMenu === "function" &&
    !!item?.id &&
    !interactionsDisabled &&
    normalizedReactions.length > 0;

  const isHost = role === "host";
  const isSystem = role === "system";
  const isVaultGridAdmin = variant === "event" && !!author_is_staff && !isSystem;
  const isDeletedSender = isDeletedUsername(sender_username) || isDeletedAccountUser(item);
  const senderDisplayName = isDeletedSender
    ? DELETED_USER_LABEL
    : formatUserHandle(sender_username);
  const senderProfileCode = isDeletedSender ? null : sender_code;
  const canOpenSenderName = !isDeletedSender && !isSupportCode(senderProfileCode);
  const canOpenSenderProfile = !isSystem && !isDeletedSender && !!senderProfileCode && !isSupportCode(senderProfileCode);
  const senderBadgeAvatarSrc = isDeletedSender ? null : avatarSrc || item?.avatarSrc || null;

  // Attachments can come in different shapes depending on backend.
  // We normalize images by looking at multiple possible fields.
  const imageUrls = useMemo(() => {
    const list = Array.isArray(attachments) ? attachments : [];

    const getType = (a) =>
      (a?.type || a?.attachment_type || "").toString().toLowerCase();
    const getUrl = (a) =>
      a?.url ||
      a?.file_url ||
      a?.external_url ||
      a?.file ||
      a?.file?.url ||
      a?.file?.file ||
      a?.file?.path ||
      null;

    return list
      .filter((a) => getType(a) === "image")
      .map((a) => getUrl(a))
      .filter(Boolean);
  }, [attachments]);

  const renderableImageUrls = useRenderableChatImageUrls(imageUrls);
  const hasImages = renderableImageUrls.length > 0;

  const pressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const pressStartRef = useRef(null);
  const rowRef = useRef(null);
  const bubbleRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pressTimerRef.current) {
        window.clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }
      document.documentElement.classList.remove("vg-chat-long-press-gesture");
    };
  }, []);

  const isFlashed = !!flashId && flashId === item?.id;
  const replyPreview = reply_to?.text || "";
  const replyAuthor = isDeletedAccountUser(reply_to)
    ? DELETED_USER_LABEL
    : formatDeletedAccountText(reply_to?.author_username || "");
  const replyId = reply_to_id || reply_to?.id || null;
  const timeLabel = edited_at && !isChatPollPayload(payload)
    ? `${t("chats.event_chat.edited")} · ${time}`
    : time;

  const rawConceptPollPayload = useMemo(() => {
    if (!payload || typeof payload !== "object") return null;
    if (String(payload?.kind || "") !== "concept_date_poll") return null;
    return payload;
  }, [payload]);
  const [localConceptPollPayload, setLocalConceptPollPayload] = React.useState(null);
  useEffect(() => {
    setLocalConceptPollPayload(null);
  }, [
    item?.id,
    rawConceptPollPayload?.poll_id,
    rawConceptPollPayload?.updated_at,
    rawConceptPollPayload?.is_closed,
  ]);
  const conceptPollPayload = localConceptPollPayload || rawConceptPollPayload;
  const chatLocationPayload = useMemo(() => getChatLocationPayload(payload), [payload]);
  const chatPollPayload = useMemo(() => getChatPollPayload(payload), [payload]);
  const chatSticker = useMemo(() => getChatStickerFromPayload(payload), [payload]);
  const galleryUploadPreview = useMemo(
    () => getEventGalleryUploadPreview(payload),
    [payload]
  );
  const galleryUploadPreviewKey = galleryUploadPreview
    ? `${galleryUploadPreview.eventId}:${galleryUploadPreview.previewMedia.map((media) => media.id).join(",")}`
    : "";
  const [galleryUploadPreviewState, setGalleryUploadPreviewState] = React.useState({
    key: "",
    status: "idle",
    items: [],
  });
  useEffect(() => {
    if (!galleryUploadPreview || !galleryUploadPreviewKey) {
      setGalleryUploadPreviewState({ key: "", status: "idle", items: [] });
      return undefined;
    }

    let cancelled = false;
    const previewIds = galleryUploadPreview.previewMedia.map((media) => media.id);
    setGalleryUploadPreviewState({
      key: galleryUploadPreviewKey,
      status: "loading",
      items: [],
    });

    api.get(`/events/${galleryUploadPreview.eventId}/gallery/`, {
      params: { media_ids: previewIds.join(",") },
      meta: { suppressGlobalError: true },
    })
      .then((response) => {
        if (cancelled) return;
        const responseItems = Array.isArray(response?.data?.items) ? response.data.items : [];
        const itemById = new Map(
          responseItems.map((media) => [Number(media?.id || 0), media])
        );
        const orderedItems = galleryUploadPreview.previewMedia
          .map((previewMedia) => {
            const media = itemById.get(previewMedia.id);
            if (!media || !String(media?.url || "").trim()) return null;
            return {
              ...media,
              id: previewMedia.id,
              type: String(media?.type || previewMedia.type).toLowerCase() === "video"
                ? "video"
                : "image",
            };
          })
          .filter(Boolean);
        setGalleryUploadPreviewState({
          key: galleryUploadPreviewKey,
          status: "ready",
          items: orderedItems,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setGalleryUploadPreviewState({
            key: galleryUploadPreviewKey,
            status: "failed",
            items: [],
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [galleryUploadPreview, galleryUploadPreviewKey]);
  const attentionRequest = useMemo(() => {
    if (!item || typeof item !== "object") return null;
    const raw = item?.attention_request;
    if (!raw || typeof raw !== "object") return null;
    return raw;
  }, [item]);
  const hasPersistentAttentionHighlight = Boolean(attentionRequest?.id);
  const targetTranslationLang = useMemo(
    () => normalizeChatTranslationLang(i18n.language),
    [i18n.language]
  );
  const isAutoTranslatedSystemBubble = message_type === "system_as_user" || isSystem;
  const translationPayload = useMemo(
    () =>
      extractTranslationPayload(payload, {
        normalizeEscapedLineBreaks: isAutoTranslatedSystemBubble,
      }),
    [isAutoTranslatedSystemBubble, payload]
  );
  const [translationLang, setTranslationLang] = React.useState(null);
  const [translationMap, setTranslationMap] = React.useState(() => translationPayload?.map || {});
  const [translationBaseLang, setTranslationBaseLang] = React.useState(
    () => translationPayload?.baseLang || null
  );
  const [translationLoading, setTranslationLoading] = React.useState(false);
  useEffect(() => {
    setTranslationLang(null);
    setTranslationLoading(false);
    setTranslationMap(translationPayload?.map || {});
    setTranslationBaseLang(translationPayload?.baseLang || null);
  }, [item?.id, translationPayload]);
  const hasTargetTranslation = Boolean(String(translationMap?.[targetTranslationLang] || "").trim());
  const canToggleTranslation =
    !is_self &&
    !isAutoTranslatedSystemBubble &&
    !chatSticker &&
    !chatLocationPayload &&
    !chatPollPayload &&
    Boolean(String(text || "").trim()) &&
    item?.id;
  const autoTranslatedSystemText =
    targetTranslationLang === translationBaseLang
      ? text
      : translationMap[targetTranslationLang] || text;
  const frontendSystemText =
    String(payload?.system_i18n_key || "").trim() === "chats.support.intro"
      ? t("chats.support.intro", { defaultValue: autoTranslatedSystemText })
      : autoTranslatedSystemText;
  const rawDisplayedText =
    isAutoTranslatedSystemBubble
      ? frontendSystemText
      : String(payload?.system_i18n_key || "").trim() === "chats.support.intro"
        ? frontendSystemText
      : canToggleTranslation && translationLang
        ? translationMap[targetTranslationLang] || text
        : text;
  const trailingSystemActionUrl = useMemo(
    () =>
      isAutoTranslatedSystemBubble
        ? extractTrailingSystemActionUrl(rawDisplayedText)
        : null,
    [isAutoTranslatedSystemBubble, rawDisplayedText]
  );
  const trailingSystemActionButton = useMemo(() => {
    if (!trailingSystemActionUrl) return null;
    return {
      label: getSystemActionUrlButtonLabel(trailingSystemActionUrl.url, t),
      to: trailingSystemActionUrl.url,
      kind: "navigate",
    };
  }, [trailingSystemActionUrl, t]);
  const eventHostContactPayload = useMemo(
    () => extractEventHostContactPayload(payload),
    [payload]
  );
  const displayedText = useMemo(
    () => {
      const normalizedText = trailingSystemActionUrl
        ? trailingSystemActionUrl.text
        : stripClubEventPublishedUrl(rawDisplayedText, payload);
      return isAutoTranslatedSystemBubble
        ? formatDeletedAccountText(normalizedText)
        : normalizedText;
    },
    [isAutoTranslatedSystemBubble, rawDisplayedText, payload, trailingSystemActionUrl]
  );
  const payloadEventLink = useMemo(() => getPayloadEventLink(payload), [payload]);
  const eventLink = useMemo(
    () =>
      payloadEventLink ||
      (!conceptPollPayload && !chatPollPayload && !chatLocationPayload && !eventHostContactPayload
        ? findChatCardLink(displayedText, parseChatEventLink)
        : null),
    [
      chatLocationPayload,
      chatPollPayload,
      payloadEventLink,
      conceptPollPayload,
      displayedText,
      eventHostContactPayload,
    ]
  );
  const [eventLinkPreview, setEventLinkPreview] = React.useState({
    eventId: null,
    status: "idle",
    event: null,
  });
  useEffect(() => {
    if (!eventLink?.eventId) {
      setEventLinkPreview({ eventId: null, status: "idle", event: null });
      return undefined;
    }

    let cancelled = false;
    const eventId = eventLink.eventId;
    setEventLinkPreview((current) =>
      current.eventId === eventId && current.status === "ready"
        ? current
        : { eventId, status: "loading", event: null }
    );
    loadChatEventDetails(eventId)
      .then((event) => {
        if (!cancelled) setEventLinkPreview({ eventId, status: "ready", event });
      })
      .catch(() => {
        if (!cancelled) setEventLinkPreview({ eventId, status: "failed", event: null });
      });

    return () => {
      cancelled = true;
    };
  }, [eventLink?.eventId]);
  const showEventLinkCard = Boolean(
    eventLink &&
      !(
        eventLinkPreview.eventId === eventLink.eventId &&
        eventLinkPreview.status === "failed" &&
        String(payload?.kind || "").trim().toLowerCase() !== "new_joiners_event_published"
      )
  );
  const showPayloadEventCard = Boolean(payloadEventLink && showEventLinkCard);
  const isNewJoinersEventPublishedCard =
    showPayloadEventCard &&
    String(payload?.kind || "").trim().toLowerCase() === "new_joiners_event_published";
  const isClubEventPublishedCard =
    showPayloadEventCard &&
    String(payload?.kind || "").trim().toLowerCase() === "club_event_published";
  const clubLink = useMemo(
    () =>
      !conceptPollPayload && !chatPollPayload && !chatLocationPayload && !eventHostContactPayload
        ? findChatCardLink(displayedText, parseChatClubLink)
        : null,
    [
      chatLocationPayload,
      chatPollPayload,
      conceptPollPayload,
      displayedText,
      eventHostContactPayload,
    ]
  );
  const [clubLinkPreview, setClubLinkPreview] = React.useState({
    clubId: null,
    status: "idle",
    club: null,
  });
  useEffect(() => {
    if (!clubLink?.clubId) {
      setClubLinkPreview({ clubId: null, status: "idle", club: null });
      return undefined;
    }

    let cancelled = false;
    const clubId = clubLink.clubId;
    setClubLinkPreview((current) =>
      current.clubId === clubId && current.status === "ready"
        ? current
        : { clubId, status: "loading", club: null }
    );
    loadChatClubDetails(clubId)
      .then((club) => {
        if (!cancelled) setClubLinkPreview({ clubId, status: "ready", club });
      })
      .catch(() => {
        if (!cancelled) setClubLinkPreview({ clubId, status: "failed", club: null });
      });

    return () => {
      cancelled = true;
    };
  }, [clubLink?.clubId]);
  const showClubLinkCard = Boolean(
    clubLink &&
      !(
        clubLinkPreview.clubId === clubLink.clubId &&
        clubLinkPreview.status === "failed"
      )
  );
  const cardCaptionText = useMemo(() => {
    if (payloadEventLink) return "";
    const ranges = [
      showEventLinkCard ? eventLink : null,
      showClubLinkCard ? clubLink : null,
    ].filter(Boolean).sort((a, b) => b.start - a.start);
    let caption = String(displayedText || "");
    for (const link of ranges) {
      caption = caption.slice(0, link.start) + caption.slice(link.end);
    }
    return caption.trim();
  }, [payloadEventLink, showEventLinkCard, eventLink, showClubLinkCard, clubLink, displayedText]);
  const bubbleColor = is_self ? CHAT_SELF_MESSAGE_BUBBLE : CHAT_OTHER_MESSAGE_BUBBLE;
  const bubbleTextClass = is_self ? "text-[#2C2C2C]" : "text-white";
  const bubbleMetaClass = is_self ? "text-[#2C2C2C]/70" : "text-white";
  const bubbleMutedClass = is_self ? "text-[#2C2C2C]/50" : "text-white/50";
  const bubbleSoftPanelClass = is_self
    ? "border-[#2C2C2C]/10 bg-[#2C2C2C]/5 text-[#2C2C2C]/80"
    : "border-white/20 bg-white/20 text-white/90";

  const facilitatorButtons = useMemo(() => {
    const payloadButtons =
      payload && typeof payload === "object" && Array.isArray(payload?.buttons)
      ? payload.buttons.filter(
          (button) =>
            button &&
            typeof button === "object" &&
            String(button.label || "").trim() &&
            String(button.to || "").trim()
        )
      : [];
    if (payloadButtons.length && !showPayloadEventCard) {
      return payloadButtons.map((button, index) => ({
        ...button,
        label:
          button?.translations?.[String(i18n.language || "").split("-")[0]] ||
          getLocalizedSystemButtonLabel(payload, button, index, t),
      }));
    }

    if (
      payloadEventLink &&
      eventLinkPreview.eventId === payloadEventLink.eventId &&
      eventLinkPreview.status === "failed"
    ) {
      const eventName = String(payload?.event_name || "").trim();
      if (eventName) {
        return [{ label: eventName, to: payloadEventLink.target, kind: "navigate" }];
      }
    }

    if (trailingSystemActionButton && !showPayloadEventCard) return [trailingSystemActionButton];

    return [];
  }, [
    payloadEventLink,
    eventLinkPreview.eventId,
    eventLinkPreview.status,
    i18n.language,
    payload,
    showPayloadEventCard,
    t,
    trailingSystemActionButton,
  ]);
  const pollOptions = useMemo(() => {
    const options = Array.isArray(conceptPollPayload?.options)
      ? conceptPollPayload.options
      : [];
    return [...options].sort(compareConceptPollOptions);
  }, [conceptPollPayload]);

  const initialMyVoteOptionIds = useMemo(() => {
    if (Array.isArray(conceptPollPayload?.my_vote_option_ids)) {
      return conceptPollPayload.my_vote_option_ids
        .map((id) => Number(id || 0))
        .filter((id) => id > 0);
    }

    const me = Number(currentUserId || 0);
    if (!me || !pollOptions.length) return [];

    return pollOptions
      .filter((opt) =>
        (Array.isArray(opt?.voters) ? opt.voters : []).some(
          (voter) => Number(voter?.user_id || 0) === me
        )
      )
      .map((opt) => Number(opt?.id || 0))
      .filter((id) => id > 0);
  }, [conceptPollPayload?.my_vote_option_ids, currentUserId, pollOptions]);

  const [pollSelectedOptionIds, setPollSelectedOptionIds] = React.useState(initialMyVoteOptionIds);
  const [pollSubmitting, setPollSubmitting] = React.useState(false);
  const [pollShowResults, setPollShowResults] = React.useState(false);
  const [chatPollResultsOpen, setChatPollResultsOpen] = React.useState(false);

  useEffect(() => {
    setPollSelectedOptionIds(initialMyVoteOptionIds);
  }, [
    conceptPollPayload?.poll_id,
    conceptPollPayload?.updated_at,
    conceptPollPayload?.is_closed,
    initialMyVoteOptionIds,
  ]);
  useEffect(() => {
    if (!chatPollPayload) setChatPollResultsOpen(false);
  }, [chatPollPayload]);
  useEffect(() => {
    if (!conceptPollPayload) setPollShowResults(false);
  }, [conceptPollPayload]);

  const sortedSelectionKey = useMemo(
    () => [...pollSelectedOptionIds].sort((a, b) => a - b).join(","),
    [pollSelectedOptionIds]
  );
  const sortedInitialSelectionKey = useMemo(
    () => [...initialMyVoteOptionIds].sort((a, b) => a - b).join(","),
    [initialMyVoteOptionIds]
  );
  const pollHasChanges = sortedSelectionKey !== sortedInitialSelectionKey;
  const pollIsClosed = Boolean(conceptPollPayload?.is_closed);
  const conceptPollEventId = Number(pollEventId || conceptPollPayload?.event_id || 0);
  const conceptPollCanVote = useMemo(() => {
    if (!conceptPollPayload || pollIsClosed || !conceptPollEventId) return false;
    if (conceptPollPayload.can_vote === false) return false;
    return true;
  }, [
    conceptPollEventId,
    conceptPollPayload,
    pollIsClosed,
  ]);
  const chatPollTotalVotes = useMemo(() => {
    if (!chatPollPayload) return 0;
    return chatPollPayload.options.reduce((sum, option) => {
      const voters = Array.isArray(option.voter_ids) ? option.voter_ids : [];
      return sum + voters.length;
    }, 0);
  }, [chatPollPayload]);
  const chatPollVotesLabel =
    !chatPollPayload
      ? ""
      : chatPollTotalVotes <= 0
        ? t("chats.event_chat.poll.no_votes", { defaultValue: "No votes" })
        : chatPollTotalVotes === 1
          ? t("chats.event_chat.poll.one_vote", { defaultValue: "1 vote" })
          : t("chats.event_chat.poll.votes_count", {
              count: chatPollTotalVotes,
              defaultValue: `${chatPollTotalVotes} votes`,
            });

  const openChatPollResults = (event) => {
    if (consumeLongPressActivation(event)) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!chatPollPayload) return;
    setChatPollResultsOpen(true);
  };

  const chatPollResultsModal = chatPollPayload ? (
    <ChatPollResultsModal
      open={chatPollResultsOpen}
      onClose={() => setChatPollResultsOpen(false)}
      pollPayload={chatPollPayload}
      votesLabel={chatPollVotesLabel}
      t={t}
    />
  ) : null;

  function renderReplyPreview({ standalone = false } = {}) {
    if (!replyPreview) return null;
    const className = standalone
      ? "mb-1 max-w-[90%] cursor-pointer truncate rounded border-l-[3px] border-white/60 bg-white/12 px-2 py-1 text-xs font-medium text-white/90"
      : `mb-1 max-w-[90%] cursor-pointer truncate rounded border-l-[3px] px-2 py-1 text-xs font-medium ${bubbleSoftPanelClass}`;

    return (
      <div
        className={className}
        onClick={(e) => {
          e.stopPropagation();
          onJumpTo?.(replyId);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onPointerCancel={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onJumpTo?.(replyId);
          }
        }}
        title={replyPreview}
        style={{ borderLeftColor: standalone || !is_self ? "#fff" : "rgba(44,44,44,0.35)" }}
      >
        {replyAuthor && <span className="font-bold mr-1">{replyAuthor}</span>}
        {replyPreview}
      </div>
    );
  }

  function renderImages() {
    if (!hasImages) return null;

    const shown = renderableImageUrls.slice(0, 4);
    const extra = renderableImageUrls.length - shown.length;

    // 1 image: big preview
    if (renderableImageUrls.length === 1) {
      const src = renderableImageUrls[0];
      return (
        <>
          <button
            type="button"
            className="w-full mb-2 rounded-[10px] overflow-hidden bg-black/20"
            onClick={(e) => {
              if (consumeLongPressActivation(e)) return;
              e.stopPropagation();
              onOpenImage?.(src, item);
            }}
            aria-label="open-image"
          >
            <img
              src={src}
              alt=""
              className="w-full h-auto max-h-[280px] select-none object-cover"
              loading="lazy"
              draggable={false}
              style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
            />
          </button>
        </>
      );
    }

    // 2+ images: simple 2-column grid
    return (
      <>
        <div className="grid grid-cols-2 gap-2 mb-2">
          {shown.map((src, idx) => {
            const isLastWithExtra = idx === shown.length - 1 && extra > 0;
            return (
              <button
                key={`${src}-${idx}`}
                type="button"
                className="relative w-full aspect-square rounded-[10px] overflow-hidden bg-black/20"
                onClick={(e) => {
                  if (consumeLongPressActivation(e)) return;
                  e.stopPropagation();
                  onOpenImage?.(src, item);
                }}
                aria-label="open-image"
              >
                <img
                  src={src}
                  alt=""
                  className="w-full h-full select-none object-cover"
                  loading="lazy"
                  draggable={false}
                  style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
                />

                {isLastWithExtra && (
                  <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                    <span className="text-white text-[18px] font-extrabold">
                      +{extra}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  function renderMediaImages({ showTimeOverlay = false } = {}) {
    if (!hasImages) return null;

    const mediaTimeNode = showTimeOverlay
      ? renderTimeLabel(
          "text-white text-[13px] font-semibold leading-none whitespace-nowrap drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
        )
      : null;

    const timeOverlay = mediaTimeNode ? (
      <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/48 px-2 py-1 shadow-[0_2px_7px_rgba(0,0,0,0.2)]">
        {mediaTimeNode}
      </span>
    ) : null;

    if (renderableImageUrls.length === 1) {
      const src = renderableImageUrls[0];
      return (
        <button
          type="button"
          className="relative block w-full overflow-hidden bg-black/18 text-left"
          onClick={(event) => {
            if (selectionMode) return;
            if (consumeLongPressActivation(event)) return;
            event.preventDefault();
            event.stopPropagation();
            onOpenImage?.(src, item);
          }}
          aria-label="open-image"
        >
          <img
            src={src}
            alt=""
            className="block h-auto max-h-[min(62vh,560px)] w-full select-none object-cover"
            loading="lazy"
            draggable={false}
            style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
          />
          {timeOverlay}
        </button>
      );
    }

    const shown = renderableImageUrls.slice(0, 4);
    const extra = renderableImageUrls.length - shown.length;

    return (
      <div className="relative grid grid-cols-2 gap-1 overflow-hidden bg-black/18">
        {shown.map((src, index) => {
          const isLastWithExtra = index === shown.length - 1 && extra > 0;
          return (
            <button
              key={`${src}-${index}`}
              type="button"
              className="relative aspect-square overflow-hidden bg-black/20"
              onClick={(event) => {
                if (selectionMode) return;
                if (consumeLongPressActivation(event)) return;
                event.preventDefault();
                event.stopPropagation();
                onOpenImage?.(src, item);
              }}
              aria-label="open-image"
            >
              <img
                src={src}
                alt=""
                className="h-full w-full select-none object-cover"
                loading="lazy"
                draggable={false}
                style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
              />
              {isLastWithExtra ? (
                <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-[18px] font-extrabold text-white">
                  +{extra}
                </span>
              ) : null}
            </button>
          );
        })}
        {timeOverlay}
      </div>
    );
  }

  const formatPollOptionLabel = (option) => {
    const rawDate = String(option?.date || "").trim();
    let datePart = rawDate;
    if (rawDate) {
      const dt = new Date(`${rawDate}T00:00:00`);
      if (!Number.isNaN(dt.getTime())) {
        datePart = dt.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    }
    const start = String(option?.start_time || "").trim();
    const end = String(option?.end_time || "").trim();
    if (start && end) {
      return `${datePart} · ${start}-${end}`;
    }
    return datePart || String(option?.label || "");
  };

  const conceptPollTotalVotes = useMemo(() => {
    if (!conceptPollPayload) return 0;
    return pollOptions.reduce((sum, option) => {
      const voters = Array.isArray(option?.voters) ? option.voters : [];
      return sum + Number(option?.votes_count || voters.length || 0);
    }, 0);
  }, [conceptPollPayload, pollOptions]);
  const conceptPollVotesLabel =
    !conceptPollPayload
      ? ""
      : conceptPollTotalVotes <= 0
        ? t("chats.event_chat.poll.no_votes", { defaultValue: "No votes" })
        : conceptPollTotalVotes === 1
          ? t("chats.event_chat.poll.one_vote", { defaultValue: "1 vote" })
          : t("chats.event_chat.poll.votes_count", {
              count: conceptPollTotalVotes,
              defaultValue: `${conceptPollTotalVotes} votes`,
            });
  const conceptPollResultsPayload = conceptPollPayload
    ? {
        title: t("chats.event_chat.poll.title"),
        options: pollOptions.map((option) => {
          const voters = Array.isArray(option?.voters) ? option.voters : [];
          return {
            id: option?.id,
            text: formatPollOptionLabel(option),
            voters,
            voter_ids: voters
              .map((voter) => Number(voter?.user_id || voter?.id || 0))
              .filter((voterId) => Number.isFinite(voterId) && voterId > 0),
          };
        }),
      }
    : null;
  const conceptPollResultsModal = conceptPollResultsPayload ? (
    <ChatPollResultsModal
      open={pollShowResults}
      onClose={() => setPollShowResults(false)}
      pollPayload={conceptPollResultsPayload}
      votesLabel={conceptPollVotesLabel}
      t={t}
    />
  ) : null;

  const togglePollOption = (optionId) => {
    if (pollIsClosed || pollSubmitting) return;
    if (conceptPollPayload && !conceptPollCanVote) return;
    const id = Number(optionId || 0);
    if (!id) return;
    setPollSelectedOptionIds((prev) => {
      const set = new Set((prev || []).map((v) => Number(v || 0)).filter(Boolean));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });
  };

  const submitConceptPollVote = async () => {
    if (
      !conceptPollPayload ||
      !conceptPollEventId ||
      !conceptPollCanVote ||
      pollIsClosed ||
      pollSubmitting
    ) {
      return;
    }
    setPollSubmitting(true);
    try {
      const response = await api.post(`/events/${conceptPollEventId}/concept-date-poll/vote/`, {
        option_ids: pollSelectedOptionIds,
      });
      const updatedPoll = response?.data?.poll;
      if (
        updatedPoll &&
        typeof updatedPoll === "object" &&
        String(updatedPoll?.kind || "") === "concept_date_poll"
      ) {
        setLocalConceptPollPayload(updatedPoll);
      }
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: { message: t("chats.event_chat.poll.vote_saved") },
        })
      );
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("chats.event_chat.poll.vote_failed");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message: msg, status },
        })
      );
    } finally {
      setPollSubmitting(false);
    }
  };

  function renderConceptPoll() {
    if (!conceptPollPayload || !pollOptions.length) return null;

    const pollCanVote = conceptPollCanVote;
    const optionDividerClass = is_self ? "divide-[#2C2C2C]/10" : "divide-white/20";
    const optionTextClass = is_self ? "text-[#2C2C2C]" : "text-white";
    const optionMutedClass = is_self ? "text-[#2C2C2C]/55" : "text-white/70";

    return (
      <>
        <div className={`${optionTextClass} text-[17px] font-bold leading-[21px]`}>
          {t("chats.event_chat.poll.title")}
        </div>
        <div className={`${optionMutedClass} mt-1 text-[14px] font-semibold leading-[18px]`}>
          {pollIsClosed
            ? t("chats.event_chat.poll.closed")
            : t("chats.event_chat.poll.description")}
        </div>

        <div className={`mt-4 divide-y ${optionDividerClass}`}>
          {pollOptions.map((option) => {
            const optionId = Number(option?.id || 0);
            const checked = pollSelectedOptionIds.includes(optionId);
            const voters = Array.isArray(option?.voters) ? option.voters : [];
            const count = Number(option?.votes_count || voters.length || 0);
            const canToggle = pollCanVote && !pollIsClosed && !pollSubmitting;
            return (
              <button
                key={`poll-opt-${optionId}`}
                type="button"
                data-testid={`concept-date-poll-option-${optionId}`}
                disabled={!canToggle}
                onClick={(e) => {
                  if (consumeLongPressActivation(e)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  togglePollOption(optionId);
                }}
                className={`flex min-h-[48px] w-full items-center gap-3 py-3 text-left transition active:scale-[0.99] ${
                  canToggle ? "cursor-pointer" : "cursor-default"
                }`}
              >
                <span
                  className={`flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-[7px] border ${
                    checked
                      ? is_self
                        ? "border-[#3E6FFB] bg-[#3E6FFB]"
                        : "border-white bg-white"
                      : is_self
                        ? "border-[#2C2C2C]/28"
                        : "border-white/65"
                  }`}
                  aria-hidden="true"
                >
                  {checked ? (
                    <span
                      className={`h-[9px] w-[9px] rounded-[3px] ${
                        is_self ? "bg-white" : "bg-[#3E6FFB]"
                      }`}
                    />
                  ) : null}
                </span>
                <span className={`${optionTextClass} min-w-0 flex-1 truncate text-[16px] font-semibold`}>
                  {formatPollOptionLabel(option)}
                </span>
                <span className={`${optionMutedClass} shrink-0 text-[15px] font-bold`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {pollCanVote && !pollIsClosed ? (
            <button
              type="button"
              onClick={(e) => {
                if (consumeLongPressActivation(e)) return;
                e.preventDefault();
                e.stopPropagation();
                void submitConceptPollVote();
              }}
              disabled={pollSubmitting || !pollHasChanges || !conceptPollEventId}
              className={`h-10 rounded-full px-4 text-[14px] font-semibold ${
                pollSubmitting || !pollHasChanges || !conceptPollEventId
                  ? is_self
                    ? "bg-[#2C2C2C]/8 text-[#2C2C2C]/38 cursor-not-allowed"
                    : "bg-white/18 text-white/55 cursor-not-allowed"
                  : is_self
                    ? "bg-[#3E6FFB] text-white"
                    : "bg-white text-[#3E6FFB]"
              }`}
            >
              {pollSubmitting
                ? t("chats.event_chat.poll.submitting")
                : sortedInitialSelectionKey
                ? t("chats.event_chat.poll.update_vote")
                : t("chats.event_chat.poll.submit_vote")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={(e) => {
              if (consumeLongPressActivation(e)) return;
              e.preventDefault();
              e.stopPropagation();
              setPollShowResults(true);
            }}
            className={`h-10 rounded-full px-4 text-[14px] font-semibold transition active:scale-[0.98] ${
              is_self ? "bg-[#2C2C2C]/8 text-[#2C2C2C]/65" : "bg-white/18 text-white/85"
            }`}
          >
            {t("chats.event_chat.poll.view_results", { defaultValue: "Review results" })}
          </button>
        </div>
      </>
    );
  }

  function renderChatPoll() {
    if (!chatPollPayload) return null;

    const pollLabel = t("chats.event_chat.poll.generic_label", { defaultValue: "Poll" });
    const optionDividerClass = is_self ? "divide-[#2C2C2C]/10" : "divide-white/20";
    const optionTextClass = is_self ? "text-[#2C2C2C]" : "text-white";
    const optionMutedClass = is_self ? "text-[#2C2C2C]/55" : "text-white/70";

    return (
      <>
        <div className={`${optionTextClass} text-[17px] font-bold leading-[21px]`}>
          {chatPollPayload.title}
        </div>
        <div className={`${optionMutedClass} mt-1 text-[14px] font-semibold leading-[18px]`}>
          {pollLabel}
        </div>
        <div className={`mt-4 divide-y ${optionDividerClass}`}>
          {chatPollPayload.options.map((option) => {
            const voters = Array.isArray(option.voter_ids) ? option.voter_ids : [];
            const voted = currentUserId != null && voters.includes(Number(currentUserId));
            const count = voters.length;
            return (
              <button
                key={option.id}
                type="button"
                disabled={!item?.id}
                onClick={(event) => {
                  if (consumeLongPressActivation(event)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  if (!item?.id) return;
                  onVotePoll?.(item, option.id);
                }}
                className="flex min-h-[48px] w-full items-start gap-3 py-3 text-left transition active:scale-[0.99]"
              >
                <span
                  className={`mt-px flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-[7px] border ${
                    voted
                      ? is_self
                        ? "border-[#3E6FFB] bg-[#3E6FFB]"
                        : "border-white bg-white"
                      : is_self
                        ? "border-[#2C2C2C]/28"
                        : "border-white/65"
                  }`}
                  aria-hidden="true"
                >
                  {voted ? (
                    <span
                      className={`h-[9px] w-[9px] rounded-[3px] ${
                        is_self ? "bg-white" : "bg-[#3E6FFB]"
                      }`}
                    />
                  ) : null}
                </span>
                <span
                  className={`${optionTextClass} min-w-0 flex-1 whitespace-normal break-words text-[16px] font-semibold leading-[21px]`}
                >
                  {option.text}
                </span>
                <span
                  className={`${optionMutedClass} mt-px shrink-0 text-[15px] font-bold leading-[21px]`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={openChatPollResults}
          className={`${optionMutedClass} mx-auto mt-2 block rounded-full px-4 py-1 text-center text-[14px] font-semibold leading-[18px] transition hover:bg-black/5 active:scale-[0.98] ${
            is_self ? "hover:text-[#2C2C2C]/70" : "hover:bg-white/10 hover:text-white/85"
          }`}
        >
          {chatPollVotesLabel}
        </button>
      </>
    );
  }

  function renderChatLocation() {
    if (!chatLocationPayload) return null;
    const fallbackAddress = `${chatLocationPayload.lat.toFixed(5)}, ${chatLocationPayload.lng.toFixed(5)}`;
    const address = formatChatLocationDisplayAddress(chatLocationPayload.address, fallbackAddress);
    const caption = String(chatLocationPayload.caption || "").trim();
    const overlayText = caption || address;
    const timeNode = renderTimeLabel(
      "text-white text-[14px] font-light leading-[17.5px] whitespace-nowrap drop-shadow-[0_1px_2px_rgba(0,0,0,0.65)]"
    );

    return (
      <>
        <ChatLocationPreviewMap
          lat={chatLocationPayload.lat}
          lng={chatLocationPayload.lng}
          authorLabel={senderDisplayName || senderProfileCode || "User"}
          className="h-[184px]"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end gap-3 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.64)_100%)] px-4 pb-3 pt-12">
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[15px] font-bold leading-[19px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
              Location
            </div>
            {overlayText ? (
              <div className="mt-0.5 truncate text-[12px] font-semibold leading-[15px] text-white/78 drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
                {overlayText}
              </div>
            ) : null}
          </div>
          {timeNode ? (
            <div className="shrink-0 rounded-full bg-black/35 px-2 py-1">
              {timeNode}
            </div>
          ) : null}
        </div>
      </>
    );
  }

  function handlePayloadButtonClick(button, event) {
    event.preventDefault();
    event.stopPropagation();

    const to = String(button?.to || "").trim();
    if (!to) return;

    if (String(button?.kind || "").trim() === "host_rules_review") {
      onOpenHostRules?.();
      return;
    }

    const target = resolvePayloadButtonTarget(to, button?.kind);
    if (target.type === "external") {
      void openExternalUrl(target.value);
      return;
    }
    if (target.type === "internal") {
      navigate(target.value);
    }
  }

  function renderFacilitatorButtons() {
    if (!facilitatorButtons.length) return null;

    return (
      <div className="flex flex-wrap justify-center gap-2">
        {facilitatorButtons.map((button, index) => (
          <button
            key={`payload-btn-${item?.id || "msg"}-${index}`}
            type="button"
            onClick={(event) => handlePayloadButtonClick(button, event)}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            onPointerCancel={(event) => event.stopPropagation()}
            title={String(button.label || "")}
            className="min-h-9 max-w-full min-w-0 px-4 py-2 rounded-xl border border-white/25 bg-white/20 text-white text-[12px] font-semibold hover:bg-white/30 transition-colors"
          >
            <span className="block truncate">{button.label}</span>
          </button>
        ))}
      </div>
    );
  }

  function handleGalleryUploadPreviewClick(event) {
    if (selectionMode) return;
    event.preventDefault();
    event.stopPropagation();

    const target = resolvePayloadButtonTarget(galleryUploadPreview?.target, "navigate");
    if (target.type === "external") {
      void openExternalUrl(target.value);
      return;
    }
    if (target.type === "internal") {
      navigate(target.value);
    }
  }

  function renderGalleryUploadPreview() {
    if (!galleryUploadPreview || !galleryUploadPreviewKey) return null;

    const stateMatches = galleryUploadPreviewState.key === galleryUploadPreviewKey;
    const isLoading = !stateMatches || galleryUploadPreviewState.status === "loading";
    if (stateMatches && galleryUploadPreviewState.status === "failed") return null;

    const previewItems = isLoading
      ? galleryUploadPreview.previewMedia.map((media) => ({ ...media, loading: true }))
      : galleryUploadPreviewState.items;
    if (!previewItems.length) return null;

    const gridClassName = previewItems.length === 1
      ? "grid-cols-1"
      : previewItems.length === 2
        ? "grid-cols-2"
        : "grid-cols-3";
    const tileHeightClassName = previewItems.length === 1
      ? "h-[138px]"
      : previewItems.length === 2
        ? "h-[112px]"
        : "h-[92px]";
    const hiddenCount = Math.max(
      0,
      galleryUploadPreview.uploadedCount - galleryUploadPreview.previewMedia.length
    );
    const openGalleryLabel = t("events:system_buttons.gallery_upload.open_gallery", {
      defaultValue: "Open gallery",
    });

    return (
      <button
        type="button"
        data-testid="event-gallery-upload-preview"
        aria-label={openGalleryLabel}
        onClick={handleGalleryUploadPreviewClick}
        onPointerDown={(event) => {
          if (!selectionMode) event.stopPropagation();
        }}
        onPointerUp={(event) => {
          if (!selectionMode) event.stopPropagation();
        }}
        onPointerCancel={(event) => {
          if (!selectionMode) event.stopPropagation();
        }}
        onContextMenu={(event) => {
          if (!selectionMode) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        className={`mt-3 grid w-[min(68vw,300px)] max-w-full overflow-hidden rounded-[17px] border p-1 text-left shadow-[0_8px_20px_rgba(0,0,0,0.14)] transition active:scale-[0.99] ${gridClassName} ${
          is_self ? "border-[#2C2C2C]/10 bg-[#2C2C2C]/5" : "border-white/20 bg-white/12"
        }`}
      >
        {previewItems.map((media, index) => {
          const showHiddenCount = hiddenCount > 0 && index === previewItems.length - 1;
          return (
            <span
              key={`gallery-preview-${media.id}-${index}`}
              className={`relative block overflow-hidden bg-[#14264A] ${tileHeightClassName} ${
                index > 0 ? "border-l-2 border-transparent" : ""
              } ${
                previewItems.length === 1
                  ? "rounded-[13px]"
                  : index === 0
                    ? "rounded-l-[13px]"
                    : index === previewItems.length - 1
                      ? "rounded-r-[13px]"
                      : ""
              }`}
            >
              {media.loading ? (
                <span className="absolute inset-0 animate-pulse bg-white/12" aria-hidden="true" />
              ) : media.type === "video" ? (
                <video
                  src={getVideoPreviewSource(media.url)}
                  preload="metadata"
                  muted
                  playsInline
                  disablePictureInPicture
                  tabIndex={-1}
                  aria-hidden="true"
                  className="pointer-events-none h-full w-full object-cover"
                />
              ) : (
                <img
                  src={media.url}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  className="pointer-events-none h-full w-full object-cover"
                />
              )}
              {!media.loading && media.type === "video" ? (
                <span
                  className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10"
                  aria-hidden="true"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/55 bg-black/45 text-white shadow-lg">
                    <svg width="15" height="17" viewBox="0 0 15 17" fill="none">
                      <path d="M14 8.5 1.5 15.7V1.3L14 8.5Z" fill="currentColor" />
                    </svg>
                  </span>
                </span>
              ) : null}
              {showHiddenCount ? (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/55 text-[22px] font-bold text-white">
                  +{hiddenCount}
                </span>
              ) : null}
            </span>
          );
        })}
      </button>
    );
  }

  const clearPressTimer = () => {
    if (pressTimerRef.current) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const clearPressGesture = () => {
    clearPressTimer();
    pressStartRef.current = null;
    document.documentElement.classList.remove("vg-chat-long-press-gesture");
  };

  const handleContextMenu = (e) => {
    if (interactionsDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    onRequestMenu?.({
      item: ctxItem,
      anchorEl: bubbleRef.current || e.currentTarget,
      rowEl: rowRef.current || e.currentTarget,
      trigger: "contextmenu",
    });
  };

  const handlePointerDown = (e) => {
    if (interactionsDisabled) return;
    const pt = e.pointerType;
    if (pt !== "touch" && pt !== "pen") return;
    clearPressTimer();
    longPressTriggeredRef.current = false;
    document.documentElement.classList.add("vg-chat-long-press-gesture");
    try {
      window.getSelection?.()?.removeAllRanges?.();
    } catch {
      // Selection cleanup is best-effort in native WebViews.
    }
    pressStartRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
    };
    pressTimerRef.current = window.setTimeout(() => {
      pressTimerRef.current = null;
      pressStartRef.current = null;
      longPressTriggeredRef.current = true;
      try {
        window.getSelection?.()?.removeAllRanges?.();
      } catch {
        // Selection cleanup is best-effort in native WebViews.
      }
      onRequestMenu?.({
        item: ctxItem,
        anchorEl: bubbleRef.current || e.currentTarget,
        rowEl: rowRef.current || e.currentTarget,
        trigger: "longpress",
      });
    }, 420);
  };

  const handlePointerMove = (event) => {
    const start = pressStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.hypot(deltaX, deltaY) > 10) clearPressGesture();
  };

  const handlePointerUp = () => clearPressGesture();
  const handlePointerCancel = () => clearPressGesture();

  const consumeLongPressActivation = (event) => {
    if (!longPressTriggeredRef.current) return false;
    longPressTriggeredRef.current = false;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    return true;
  };

  const renderTimeLabel = (className = "") => {
    if (!timeLabel) return null;
    return (
      <span className={`${className} inline-flex items-center gap-1`}>
        {isPendingDelivery ? (
          <PendingDeliveryIndicator className="shrink-0 opacity-65" />
        ) : showDirectReadReceipt ? (
          <DirectDeliveryChecks
            read={isDirectMessageReadByPeer}
            className="shrink-0"
          />
        ) : null}
        {timeLabel}
      </span>
    );
  };

  const hasBubbleReactions = normalizedReactions.length > 0;
  const isLargeEmojiMessage =
    isEmojiOnly && emojiCount > 0 && emojiCount <= 3 && !isSystem;
  const shouldInlineTimeWithText =
    !hasBubbleReactions &&
    !!displayedText &&
    !conceptPollPayload &&
    !chatPollPayload &&
    !chatLocationPayload &&
    !isLargeEmojiMessage &&
    !eventHostContactPayload &&
    !showEventLinkCard &&
    !showPayloadEventCard &&
    !showClubLinkCard;

  const renderTextContent = () => {
    // Poll messages already include dedicated title/description UI above.
    // Keep only the timestamp / reactions in the meta row below.
    if (conceptPollPayload || chatPollPayload || chatLocationPayload || !displayedText) return null;

    if (eventHostContactPayload) {
      return (
        <DirectChatEventContextNotice
          event={eventHostContactPayload.event}
          title={displayedText}
        />
      );
    }

    // Большие эмодзи: сообщение только из эмодзи, 1–3 штуки, не system
    if (isLargeEmojiMessage) {
      return (
        <div
          className={`${bubbleTextClass} text-[40px] leading-[44px] whitespace-pre-wrap break-words ${
            is_self ? "text-right" : "text-left"
          }`}
        >
          {displayedText}
        </div>
      );
    }

    if (shouldInlineTimeWithText) {
      return (
        <div className={`${bubbleTextClass} text-[15px] font-normal leading-[20.5px] whitespace-pre-wrap break-words min-w-0`}>
          {renderTextWithUsernameLinks(
            displayedText,
            item?.onSystemUsernameClick,
            allowedMentionSet,
            is_self ? "self" : "other"
          )}
          {renderTimeLabel(
            `float-right ml-2 ${bubbleMetaClass} text-[14px] font-light leading-[18px] whitespace-nowrap align-bottom relative top-[2px]`
          )}
          <span aria-hidden className="block clear-both h-0" />
        </div>
      );
    }

    return (
      <div className={`${bubbleTextClass} text-[15px] font-normal leading-[20.5px] whitespace-pre-wrap break-words min-w-0`}>
        {renderTextWithUsernameLinks(
          displayedText,
          item?.onSystemUsernameClick,
          allowedMentionSet,
          is_self ? "self" : "other"
        )}
      </div>
    );
  };

  const handleSelectClick = (event) => {
    if (!selectionMode) return;
    event.preventDefault();
    event.stopPropagation();
    if (!isSelectableMessage) return;
    onSelectMessage?.(item);
  };

  const wrapperProps = interactionsDisabled
    ? {}
    : selectionMode
      ? {
          onClick: handleSelectClick,
        }
      : {
          onContextMenu: handleContextMenu,
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
          onPointerCancel: handlePointerCancel,
          onPointerLeave: handlePointerUp,
          "data-chat-message-gesture-surface": "true",
          "data-testid": item?.id ? `chat-message-row-${item.id}` : undefined,
        };

  const bubbleHighlightClass = [
    hasPersistentAttentionHighlight ? "outline outline-2 outline-white/95" : "",
    isSelectedMessage ? "ring-2 ring-white/90" : "",
    isFlashed ? "ring-2 ring-white/60" : "",
    selectionMode && isSelectableMessage ? "cursor-pointer border border-dashed border-white/60" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const facilitatorButtonsNode = renderFacilitatorButtons();
  const toggleTranslation = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canToggleTranslation) return;

    if (translationLang) {
      setTranslationLang(null);
      return;
    }

    if (hasTargetTranslation) {
      setTranslationLang(targetTranslationLang);
      return;
    }

    try {
      setTranslationLoading(true);
      const resp = await api.post(`/chats/internal-chat/messages/${item.id}/translate/`, {
        target_lang: targetTranslationLang,
      });
      const translatedText = String(resp?.data?.text || "").trim();
      const nextPayload = resp?.data?.payload || null;
      const nextTranslationPayload = extractTranslationPayload(nextPayload, {
        normalizeEscapedLineBreaks: isAutoTranslatedSystemBubble,
      });
      if (nextTranslationPayload?.map) {
        setTranslationMap((prev) => ({ ...prev, ...nextTranslationPayload.map }));
        setTranslationBaseLang(nextTranslationPayload.baseLang || translationBaseLang || null);
      } else if (translatedText) {
        setTranslationMap((prev) => ({ ...prev, [targetTranslationLang]: translatedText }));
      }
      if (translatedText) {
        setTranslationLang(targetTranslationLang);
      }
    } catch (error) {
      const backendMessage = String(error?.response?.data?.error || "").trim();
      showTranslationError(backendMessage);
    } finally {
      setTranslationLoading(false);
    }
  };

  const translateButtonLabel = translationLang
    ? "Original"
    : translationLoading
      ? "Translating..."
      : "Translate";

  function renderReactionChips({ insideBubble = false } = {}) {
    if (!normalizedReactions.length) return null;

    return (
      <div
        className={`flex min-w-0 flex-wrap items-center gap-1.5 ${
          insideBubble ? "max-w-full" : "max-w-[82%]"
        }`}
      >
        {normalizedReactions.map((reaction) => {
          const isSelfReaction = !!reaction?.is_self;
          const label = `${reaction?.emoji || ""} ${reaction?.count || 0}`.trim();
          const isInteractive = canOpenReactionDetails || canReact;
          return (
            <button
              key={`${item?.id || "msg"}-${reaction?.emoji}`}
              type="button"
              disabled={!isInteractive}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (canOpenReactionDetails && bubbleRef.current) {
                  onRequestMenu?.({
                    item: ctxItem,
                    anchorEl: bubbleRef.current,
                    rowEl: rowRef.current || bubbleRef.current,
                    openDetailPanel: "reactions",
                    reactionEmoji: reaction?.emoji || null,
                  });
                  return;
                }
                onToggleReaction?.(item, reaction?.emoji);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onPointerCancel={(event) => event.stopPropagation()}
              className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-semibold transition ${
                insideBubble
                  ? isSelfReaction
                    ? is_self
                      ? "border-[#2C2C2C]/15 bg-[#2C2C2C]/10 text-[#2C2C2C]"
                      : "border-white/80 bg-white text-[#002A5D]"
                    : is_self
                      ? "border-[#2C2C2C]/10 bg-[#2C2C2C]/5 text-[#2C2C2C]/75"
                      : "border-white/20 bg-black/15 text-white"
                  : isSelfReaction
                    ? "border-white/80 bg-white text-[#002A5D]"
                    : "border-white/25 bg-white/12 text-white"
              } ${isInteractive ? "active:scale-[0.98]" : "cursor-default"}`}
              aria-label={label}
            >
              <span className="text-[15px] leading-none">{reaction?.emoji}</span>
              {Number(reaction?.count || 0) > 1 ? (
                <span className="leading-none">{reaction.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  const renderBubbleMeta = () => {
    const reactionsNode = renderReactionChips({ insideBubble: true });
    const timeNode = renderTimeLabel(
      `${bubbleMetaClass} text-[14px] font-light leading-[18px] whitespace-nowrap`
    );

    if (shouldInlineTimeWithText && !reactionsNode) return null;
    if (!reactionsNode && !timeNode) return null;

    const compactMeta = conceptPollPayload || chatPollPayload || chatLocationPayload || !text || isLargeEmojiMessage;

    return (
      <div className={`${compactMeta ? "mt-1" : "mt-2"} flex items-end gap-2 min-w-0`}>
        <div className="min-w-0 flex-1">{reactionsNode}</div>
        {timeNode ? <div className="shrink-0">{timeNode}</div> : null}
      </div>
    );
  }

  const isImageMediaMessage =
    hasImages &&
    !chatLocationPayload &&
    !chatPollPayload &&
    !conceptPollPayload &&
    !eventHostContactPayload;

  const mediaCaptionText = String(displayedText || "").trim();
  const hasMediaCaption = Boolean(mediaCaptionText);

  function renderMediaCaption() {
    if (!hasMediaCaption && !canToggleTranslation) return null;

    return (
      <div
        className={`px-3 py-2.5 ${
          is_self ? "bg-white text-[#2C2C2C]" : "bg-[#7498FF] text-white"
        }`}
      >
        {canToggleTranslation ? (
          <div className="mb-1 flex justify-end">
            <button
              type="button"
              onClick={toggleTranslation}
              disabled={translationLoading}
              className={`text-[13px] font-light underline underline-offset-2 disabled:opacity-60 ${
                is_self ? "text-[#2C2C2C]/55" : "text-white/72"
              }`}
            >
              {translateButtonLabel}
            </button>
          </div>
        ) : null}
        {hasMediaCaption ? (
          <div className="min-w-0 whitespace-pre-wrap break-words text-[15px] font-normal leading-[20.5px]">
            {renderTextWithUsernameLinks(
              mediaCaptionText,
              item?.onSystemUsernameClick,
              allowedMentionSet,
              is_self ? "self" : "other"
            )}
            {renderTimeLabel(
              `float-right ml-2 ${
                is_self ? "text-[#2C2C2C]/62" : "text-white/78"
              } text-[13px] font-light leading-[18px] whitespace-nowrap align-bottom relative top-[2px]`
            )}
            <span aria-hidden className="block clear-both h-0" />
          </div>
        ) : null}
      </div>
    );
  }

  function renderEventLinkCard() {
    if (!eventLink || !showEventLinkCard) return null;

    const previewMatches = eventLinkPreview.eventId === eventLink.eventId;
    const event = previewMatches ? eventLinkPreview.event : null;
    const loading = !previewMatches || eventLinkPreview.status !== "ready";
    const title = String(event?.name || event?.title || "").trim();
    const isConcept = String(event?.type || payload?.item_label || "").toLowerCase() === "concept";
    const dateLabel = isConcept ? "" : formatChatEventDate(
      event?.date_time || event?.datetime || event?.start_time,
      i18n.language
    );
    const imageSrc = resolveChatEventImageSrc(event);

    return (
      <article
        data-testid="chat-event-link-card"
        className="relative min-h-[148px] w-full overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#A2AAB8_0%,#737D90_52%,#505969_100%)] text-white shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0)_34%),linear-gradient(135deg,#A8B0BD_0%,#737D8E_54%,#4D5665_100%)]"
            aria-hidden="true"
          />
        )}
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,17,38,0.80)_0%,rgba(7,17,38,0.50)_46%,rgba(7,17,38,0.72)_100%)]"
          aria-hidden="true"
        />
        <div className="relative z-10 flex min-h-[148px] flex-col px-4 py-3">
          {loading ? (
            <>
              <span className="h-5 w-[74%] animate-pulse rounded-full bg-white/25" aria-hidden="true" />
              {!isConcept ? <span className="mt-2 h-4 w-[48%] animate-pulse rounded-full bg-white/18" aria-hidden="true" /> : null}
            </>
          ) : (
            <>
              <h3 className="line-clamp-2 text-[16px] font-bold leading-5 tracking-[-0.01em]">
                {title || t("chats.event_chat.event_link.event_fallback", { defaultValue: "Event" })}
              </h3>
              {dateLabel ? (
                <p className="mt-1 text-[13px] font-medium leading-[18px] text-white/88">
                  {dateLabel}
                </p>
              ) : null}
            </>
          )}
          <button
            type="button"
            disabled={loading}
            onClick={(event) => {
              if (selectionMode) {
                handleSelectClick(event);
                return;
              }
              if (consumeLongPressActivation(event)) return;
              event.preventDefault();
              event.stopPropagation();
              navigate(eventLink.target);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            onPointerCancel={(event) => event.stopPropagation()}
            className="mt-auto flex h-10 w-full items-center justify-center rounded-full bg-white/95 px-4 text-[13px] font-semibold leading-4 text-[#3E6FFB] shadow-[0_8px_16px_rgba(0,0,0,0.18)] transition active:scale-[0.98] disabled:opacity-60"
          >
            {loading
              ? t("chats.event_chat.event_link.loading", { defaultValue: "Loading event…" })
              : isConcept
                ? t("chats.event_chat.event_link.open_concept", { defaultValue: "Open concept" })
                : t("chats.event_chat.event_link.open_event", { defaultValue: "Open event" })}
          </button>
        </div>
      </article>
    );
  }

  function renderPayloadEventHeader() {
    if (!showPayloadEventCard || !displayedText) return null;

    return (
      <div className="rounded-[22px] bg-[#7498FF] px-4 py-3 text-white shadow-[0_10px_24px_rgba(0,0,0,0.14)]">
        <div className="text-[15px] font-normal leading-[20.5px] whitespace-pre-wrap break-words">
          {renderTextWithUsernameLinks(
            displayedText,
            item?.onSystemUsernameClick,
            allowedMentionSet,
            "other"
          )}
          {renderTimeLabel(
            "float-right ml-2 text-white/78 text-[14px] font-light leading-[18px] whitespace-nowrap align-bottom relative top-[2px]"
          )}
          <span aria-hidden className="block clear-both h-0" />
        </div>
      </div>
    );
  }

  function renderClubLinkCard() {
    if (!clubLink || !showClubLinkCard) return null;

    const previewMatches = clubLinkPreview.clubId === clubLink.clubId;
    const club = previewMatches ? clubLinkPreview.club : null;
    const loading = !previewMatches || clubLinkPreview.status !== "ready";
    const title = String(club?.name || club?.title || "").trim();
    const membersCount = Math.max(
      0,
      Number(club?.members_count ?? club?.member_count ?? club?.total_members ?? 0) || 0
    );
    const imageSrc = resolveChatClubImageSrc(club);

    return (
      <article
        data-testid="chat-club-link-card"
        className="relative min-h-[148px] w-full overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#A2AAB8_0%,#737D90_52%,#505969_100%)] text-white shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0)_34%),linear-gradient(135deg,#A8B0BD_0%,#737D8E_54%,#4D5665_100%)]"
            aria-hidden="true"
          />
        )}
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.58)_0%,rgba(0,0,0,0.34)_46%,rgba(0,0,0,0.66)_100%)]"
          aria-hidden="true"
        />
        <div className="relative z-10 flex min-h-[148px] flex-col px-4 py-3">
          {loading ? (
            <>
              <span className="h-5 w-[74%] animate-pulse rounded-full bg-white/25" aria-hidden="true" />
              <span className="mt-2 h-4 w-[48%] animate-pulse rounded-full bg-white/18" aria-hidden="true" />
            </>
          ) : (
            <>
              <h3 className="line-clamp-2 text-[16px] font-bold leading-5 tracking-[-0.01em]">
                {title || t("chats.event_chat.club_link.club_fallback", { defaultValue: "Club" })}
              </h3>
              <p className="mt-1 text-[13px] font-medium leading-[18px] text-white/88">
                {t("clubs.public.members_count", {
                  ns: "communities",
                  count: membersCount,
                  defaultValue: `${membersCount} members`,
                })}
              </p>
            </>
          )}
          <button
            type="button"
            disabled={loading}
            onClick={(event) => {
              if (selectionMode) {
                handleSelectClick(event);
                return;
              }
              if (consumeLongPressActivation(event)) return;
              event.preventDefault();
              event.stopPropagation();
              navigate(clubLink.target);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            onPointerCancel={(event) => event.stopPropagation()}
            className="mt-auto flex h-10 w-full items-center justify-center rounded-full bg-white/95 px-4 text-[13px] font-semibold leading-4 text-[#3E6FFB] shadow-[0_8px_16px_rgba(0,0,0,0.18)] transition active:scale-[0.98] disabled:opacity-60"
          >
            {loading
              ? t("chats.event_chat.club_link.loading", { defaultValue: "Loading club…" })
              : t("chats.event_chat.club_link.open_club", { defaultValue: "Open club" })}
          </button>
        </div>
      </article>
    );
  }

  if (chatSticker) {
    const stickerShellClassName = [
      "inline-flex w-[min(48vw,220px)] min-w-[136px] max-w-[58%] flex-col",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");
    const stickerReactionsNode = renderReactionChips({ insideBubble: false });
    const stickerTimeNode = renderTimeLabel(
      "rounded-full bg-black/35 px-2 py-0.5 text-[12px] font-medium leading-4 text-white/90 backdrop-blur-sm whitespace-nowrap"
    );
    const stickerBody = (
      <div ref={bubbleRef} data-bubble-id={item?.id} className={stickerShellClassName}>
        {renderReplyPreview({ standalone: true })}
        <img
          src={chatSticker.src}
          alt={t("chats.event_chat.stickers.message_alt", { defaultValue: "Sticker" })}
          draggable={false}
          className="aspect-square h-auto w-full select-none object-contain drop-shadow-[0_12px_16px_rgba(0,0,0,0.2)]"
          data-testid="chat-sticker-message"
        />
        {stickerReactionsNode || stickerTimeNode ? (
          <div className="mt-[-2px] flex min-w-0 items-end gap-2">
            <div className="min-w-0 flex-1">{stickerReactionsNode}</div>
            {stickerTimeNode ? <div className="shrink-0">{stickerTimeNode}</div> : null}
          </div>
        ) : null}
      </div>
    );

    return (
      <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
        <div
          ref={rowRef}
          className={`flex w-full items-end ${is_self ? "justify-end" : ""} ${
            !is_self && !hideSenderBadge ? "gap-2" : ""
          }`}
          data-msg-id={item?.id}
          {...wrapperProps}
        >
          {!is_self && !hideSenderBadge ? (
            <SenderBadge
              code={senderProfileCode}
              isPremium={!isDeletedSender && !!sender_has_premium_subscription}
              avatarSrc={senderBadgeAvatarSrc}
              disabled={!canOpenSenderProfile}
            />
          ) : null}
          {stickerBody}
        </div>
      </div>
    );
  }

  if (isImageMediaMessage) {
    const mediaShellClassName = [
      "inline-flex w-[min(74vw,360px)] min-w-[170px] max-w-[82%] flex-col",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");
    const mediaCardClassName = [
      "overflow-hidden rounded-[18px] shadow-[0_10px_24px_rgba(0,0,0,0.16)]",
      hasMediaCaption ? (is_self ? "bg-white" : "bg-[#7498FF]") : "bg-transparent",
    ]
      .filter(Boolean)
      .join(" ");
    const mediaReactionsNode = renderReactionChips({ insideBubble: true });
    const mediaCard = (
      <div className={mediaCardClassName}>
        {renderMediaImages({ showTimeOverlay: !hasMediaCaption })}
        {renderMediaCaption()}
      </div>
    );

    if (is_self) {
      return (
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className="flex justify-end w-full"
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            <div ref={bubbleRef} data-bubble-id={item?.id} className={mediaShellClassName}>
              {renderReplyPreview({ standalone: true })}
              {mediaCard}
              {mediaReactionsNode ? <div className="mt-1">{mediaReactionsNode}</div> : null}
            </div>
          </div>
          {facilitatorButtonsNode ? (
            <div className="mt-2 flex justify-end">
              <div className="inline-flex max-w-[82%] justify-center">
                {facilitatorButtonsNode}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
        <div
          ref={rowRef}
          className={`flex items-end w-full ${hideSenderBadge ? "" : "gap-2"}`}
          data-msg-id={item?.id}
          {...wrapperProps}
        >
          {!hideSenderBadge && (
            <SenderBadge
              code={senderProfileCode}
              isPremium={!isDeletedSender && !!sender_has_premium_subscription}
              avatarSrc={senderBadgeAvatarSrc}
              disabled={!canOpenSenderProfile}
            />
          )}
          <div ref={bubbleRef} data-bubble-id={item?.id} className={mediaShellClassName}>
            {renderReplyPreview({ standalone: true })}
            {mediaCard}
            {mediaReactionsNode ? <div className="mt-1">{mediaReactionsNode}</div> : null}
          </div>
        </div>
        {facilitatorButtonsNode ? (
          <div
            className={`mt-2 flex ${hideSenderBadge ? "justify-start" : "pl-11 justify-start"}`}
          >
            <div className="w-full max-w-[82%] flex justify-center">
              {facilitatorButtonsNode}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (showEventLinkCard || showClubLinkCard) {
    const eventCardShellClassName = [
      "inline-flex w-[min(78vw,380px)] min-w-[250px] max-w-[86%] flex-col",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");
    const eventCardReactionsNode = renderReactionChips({ insideBubble: true });
    const eventCardMetaNode = showPayloadEventCard ? null : renderTimeLabel(
      "text-[12px] font-light leading-4 text-white/62 whitespace-nowrap"
    );
    const eventCardBody = (
      <div ref={bubbleRef} data-bubble-id={item?.id} className={eventCardShellClassName}>
        {renderReplyPreview({ standalone: true })}
        {isNewJoinersEventPublishedCard ? (
          <>
            {renderPayloadEventHeader()}
            <div className={displayedText ? "mt-2" : ""}>{renderEventLinkCard()}</div>
          </>
        ) : isClubEventPublishedCard ? (
          <>
            <div>{renderEventLinkCard()}</div>
            <div className="mt-2">{renderPayloadEventHeader()}</div>
          </>
        ) : (
          <>
            {renderPayloadEventHeader()}
            {cardCaptionText ? (
              <div
                data-testid="chat-link-caption"
                className={`rounded-[25px] px-5 py-3 ${bubbleTextClass}`}
                style={{ backgroundColor: bubbleColor }}
              >
                {canToggleTranslation ? (
                  <div className="mb-1 flex justify-end">
                    <button type="button" onClick={toggleTranslation} disabled={translationLoading}
                      className={`text-[13px] font-light underline underline-offset-2 disabled:opacity-60 ${bubbleMutedClass}`}>
                      {translateButtonLabel}
                    </button>
                  </div>
                ) : null}
                <div className="whitespace-pre-wrap break-words text-[15px] leading-[20.5px]">
                  {renderTextWithUsernameLinks(cardCaptionText, item?.onSystemUsernameClick, allowedMentionSet, is_self ? "self" : "other")}
                </div>
              </div>
            ) : null}
            <div className={`${showPayloadEventCard || cardCaptionText ? "mt-2" : ""} space-y-2`}>
              {renderEventLinkCard()}
              {renderClubLinkCard()}
            </div>
          </>
        )}
        {eventCardReactionsNode || eventCardMetaNode ? (
          <div className="mt-1 flex min-w-0 items-end gap-2">
            <div className="min-w-0 flex-1">{eventCardReactionsNode}</div>
            {eventCardMetaNode ? <div className="shrink-0">{eventCardMetaNode}</div> : null}
          </div>
        ) : null}
      </div>
    );

    if (is_self) {
      return (
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className="flex w-full justify-end"
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            {eventCardBody}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
        <div
          ref={rowRef}
          className={`flex w-full items-end ${hideSenderBadge ? "" : "gap-2"}`}
          data-msg-id={item?.id}
          {...wrapperProps}
        >
          {!hideSenderBadge ? (
            <SenderBadge
              code={senderProfileCode}
              isPremium={!isDeletedSender && !!sender_has_premium_subscription}
              avatarSrc={senderBadgeAvatarSrc}
              disabled={!canOpenSenderProfile}
            />
          ) : null}
          {eventCardBody}
        </div>
      </div>
    );
  }

  if (chatLocationPayload) {
    const locationShellClassName = [
      "inline-flex w-[82%] min-w-[240px] max-w-[360px] flex-col",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");
    const locationCardClassName = [
      "relative block w-full overflow-hidden rounded-[25px] border bg-[#1E345D] text-left shadow-[0_12px_28px_rgba(0,0,0,0.16)] transition active:scale-[0.99]",
      is_self ? "border-[#3E6FFB]/85" : "border-white/18",
    ]
      .filter(Boolean)
      .join(" ");
    const locationReactionsNode = renderReactionChips({ insideBubble: true });
    const locationButton = (
      <button
        type="button"
        onClick={(event) => {
          if (selectionMode) return;
          if (consumeLongPressActivation(event)) return;
          event.preventDefault();
          event.stopPropagation();
          onOpenLocation?.(item);
        }}
        className={locationCardClassName}
      >
        {renderChatLocation()}
      </button>
    );

    if (is_self) {
      return (
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className="flex justify-end w-full"
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            <div ref={bubbleRef} data-bubble-id={item?.id} className={locationShellClassName}>
              {renderReplyPreview({ standalone: true })}
              {locationButton}
              {locationReactionsNode ? (
                <div className="mt-1">{locationReactionsNode}</div>
              ) : null}
            </div>
          </div>
          {facilitatorButtonsNode ? (
            <div className="mt-2 flex justify-end">
              <div className="inline-flex max-w-[82%] justify-center">
                {facilitatorButtonsNode}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
        <div
          ref={rowRef}
          className={`flex items-end w-full ${hideSenderBadge ? "" : "gap-2"}`}
          data-msg-id={item?.id}
          {...wrapperProps}
        >
          {!hideSenderBadge && (
            <SenderBadge
              code={senderProfileCode}
              isPremium={!isDeletedSender && !!sender_has_premium_subscription}
              avatarSrc={senderBadgeAvatarSrc}
              disabled={!canOpenSenderProfile}
            />
          )}
          <div ref={bubbleRef} data-bubble-id={item?.id} className={locationShellClassName}>
            {renderReplyPreview({ standalone: true })}
            {locationButton}
            {locationReactionsNode ? (
              <div className="mt-1">{locationReactionsNode}</div>
            ) : null}
          </div>
        </div>
        {facilitatorButtonsNode ? (
          <div
            className={`mt-2 flex ${hideSenderBadge ? "justify-start" : "pl-11 justify-start"}`}
          >
            <div className="w-full max-w-[82%] flex justify-center">
              {facilitatorButtonsNode}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (conceptPollPayload) {
    const conceptPollCardClassName = [
      "inline-flex w-auto min-w-[260px] max-w-[82%] flex-col rounded-[25px] px-5 py-4 shadow-[0_12px_28px_rgba(0,0,0,0.12)]",
      is_self ? "bg-white" : "bg-[#7498FF]",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");

    if (is_self) {
      return (
        <>
          <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
            <div
              ref={rowRef}
              className="flex justify-end w-full"
              data-msg-id={item?.id}
              {...wrapperProps}
            >
              <div ref={bubbleRef} data-bubble-id={item?.id} className={conceptPollCardClassName}>
                {renderReplyPreview()}
                {renderConceptPoll()}
                {renderBubbleMeta()}
              </div>
            </div>
            {facilitatorButtonsNode ? (
              <div className="mt-2 flex justify-end">
                <div className="inline-flex max-w-[82%] justify-center">
                  {facilitatorButtonsNode}
                </div>
              </div>
            ) : null}
          </div>
          {conceptPollResultsModal}
        </>
      );
    }

    return (
      <>
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className={`flex items-end w-full ${hideSenderBadge ? "" : "gap-2"}`}
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            {!hideSenderBadge && (
              <SenderBadge
                code={senderProfileCode}
                isPremium={!isDeletedSender && !!sender_has_premium_subscription}
                avatarSrc={senderBadgeAvatarSrc}
                disabled={!canOpenSenderProfile}
              />
            )}
            <div ref={bubbleRef} data-bubble-id={item?.id} className={conceptPollCardClassName}>
              {!hideSenderBadge && (
                <div className="mb-2 min-w-0 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2 grow">
                    <span className="min-w-0 truncate text-[14px] font-bold text-white">
                      {senderDisplayName}
                    </span>
                    {(isVaultGridAdmin || isSystem || isHost) && (
                      <span className="rounded-full bg-white/65 px-2 py-[2px] text-[11px] font-medium leading-none text-[#2C2C2C] whitespace-nowrap lowercase shrink-0">
                        {isVaultGridAdmin
                          ? t("chats.roles.vaultgrid_admin")
                          : isSystem
                            ? t("chats.roles.system")
                            : t("chats.roles.host")}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {renderReplyPreview()}
              {renderConceptPoll()}
              {renderBubbleMeta()}
            </div>
          </div>
          {facilitatorButtonsNode ? (
            <div
              className={`mt-2 flex ${hideSenderBadge ? "justify-start" : "pl-11 justify-start"}`}
            >
              <div className="w-full max-w-[82%] flex justify-center">
                {facilitatorButtonsNode}
              </div>
            </div>
          ) : null}
        </div>
        {conceptPollResultsModal}
      </>
    );
  }

  if (chatPollPayload) {
    const pollCardClassName = [
      "inline-flex w-auto min-w-[240px] max-w-[82%] flex-col rounded-[25px] px-5 py-4 shadow-[0_12px_28px_rgba(0,0,0,0.12)]",
      is_self ? "bg-white" : "bg-[#7498FF]",
      bubbleHighlightClass,
    ]
      .filter(Boolean)
      .join(" ");

    if (is_self) {
      return (
        <>
          <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
            <div
              ref={rowRef}
              className="flex justify-end w-full"
              data-msg-id={item?.id}
              {...wrapperProps}
            >
              <div ref={bubbleRef} data-bubble-id={item?.id} className={pollCardClassName}>
                {renderReplyPreview()}
                {renderChatPoll()}
                {renderBubbleMeta()}
              </div>
            </div>
            {facilitatorButtonsNode ? (
              <div className="mt-2 flex justify-end">
                <div className="inline-flex max-w-[82%] justify-center">
                  {facilitatorButtonsNode}
                </div>
              </div>
            ) : null}
          </div>
          {chatPollResultsModal}
        </>
      );
    }

    return (
      <>
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className={`flex items-end w-full ${hideSenderBadge ? "" : "gap-2"}`}
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            {!hideSenderBadge && (
              <SenderBadge
                code={senderProfileCode}
                isPremium={!isDeletedSender && !!sender_has_premium_subscription}
                avatarSrc={senderBadgeAvatarSrc}
                disabled={!canOpenSenderProfile}
              />
            )}
            <div ref={bubbleRef} data-bubble-id={item?.id} className={pollCardClassName}>
              {!hideSenderBadge && (
                <div className="mb-2 min-w-0 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2 grow">
                    <span className="min-w-0 truncate text-[14px] font-bold text-white">
                      {senderDisplayName}
                    </span>
                    {(isVaultGridAdmin || isSystem || isHost) && (
                      <span className="rounded-full bg-white/65 px-2 py-[2px] text-[11px] font-medium leading-none text-[#2C2C2C] whitespace-nowrap lowercase shrink-0">
                        {isVaultGridAdmin
                          ? t("chats.roles.vaultgrid_admin")
                          : isSystem
                            ? t("chats.roles.system")
                            : t("chats.roles.host")}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {renderReplyPreview()}
              {renderChatPoll()}
              {renderBubbleMeta()}
            </div>
          </div>
          {facilitatorButtonsNode ? (
            <div
              className={`mt-2 flex ${hideSenderBadge ? "justify-start" : "pl-11 justify-start"}`}
            >
              <div className="w-full max-w-[82%] flex justify-center">
                {facilitatorButtonsNode}
              </div>
            </div>
          ) : null}
        </div>
        {chatPollResultsModal}
      </>
    );
  }

  if (is_self) {
    return (
      <>
        <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
          <div
            ref={rowRef}
            className="flex justify-end w-full"
            data-msg-id={item?.id}
            {...wrapperProps}
          >
            <div
              ref={bubbleRef}
              data-bubble-id={item?.id}
              className={`inline-flex w-auto min-w-0 max-w-[82%] flex-col rounded-[25px] px-5 py-3 ${bubbleHighlightClass}`}
              style={{ backgroundColor: bubbleColor }}
            >
              {canToggleTranslation ? (
                <div className="mb-1 flex justify-end">
                  <button
                    type="button"
                    onClick={toggleTranslation}
                    disabled={translationLoading}
                    className={`${bubbleMutedClass} text-[14px] font-light underline underline-offset-2 disabled:opacity-60`}
                  >
                    {translateButtonLabel}
                  </button>
                </div>
              ) : null}
              {renderReplyPreview()}
              {renderImages()}
              {renderChatLocation()}
              {renderChatPoll()}
              {renderConceptPoll()}
              {renderTextContent()}
              {renderGalleryUploadPreview()}
              {renderBubbleMeta()}
            </div>
          </div>
          {facilitatorButtonsNode ? (
            <div className="mt-2 flex justify-end">
              <div className="inline-flex max-w-[82%] justify-center">
                {facilitatorButtonsNode}
              </div>
            </div>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-3 w-full" style={{ opacity: isContextHidden ? 0 : 1 }}>
        <div
          ref={rowRef}
          className={`flex items-end w-full ${hideSenderBadge ? "" : "gap-2"}`}
          data-msg-id={item?.id}
          {...wrapperProps}
        >
          {!hideSenderBadge && (
            <SenderBadge
              code={senderProfileCode}
              isPremium={!isDeletedSender && !!sender_has_premium_subscription}
              avatarSrc={senderBadgeAvatarSrc}
              disabled={!canOpenSenderProfile}
            />
          )}

          <div
            ref={bubbleRef}
            data-bubble-id={item?.id}
            className={`inline-block w-fit min-w-0 max-w-full rounded-[25px] px-5 py-3 ${bubbleHighlightClass}`}
            style={{ backgroundColor: bubbleColor }}
          >
            {!hideSenderBadge && (
              <>
                <div className="mb-1 min-w-0 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2 grow">
                    <span
                      className={`${bubbleTextClass} text-[15px] font-bold min-w-0 truncate ${canOpenSenderName ? "cursor-pointer" : "cursor-default"}`}
                      role={canOpenSenderName ? "link" : undefined}
                      tabIndex={canOpenSenderName ? 0 : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!canOpenSenderName) return;
                        const u = String(sender_username || "").trim();
                        if (!u) return;
                        const withAt = u.startsWith("@") ? u : `@${u}`;
                        item?.onSystemUsernameClick?.(withAt);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canOpenSenderName) return;
                          const u = String(sender_username || "").trim();
                          if (!u) return;
                          const withAt = u.startsWith("@") ? u : `@${u}`;
                          item?.onSystemUsernameClick?.(withAt);
                        }
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerUp={(e) => e.stopPropagation()}
                      onPointerCancel={(e) => e.stopPropagation()}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      title={(() => {
                        return senderDisplayName || "";
                      })()}
                    >
                      {senderDisplayName}
                    </span>
                    {(isVaultGridAdmin || isSystem || isHost) && (
                      <span className="rounded-full bg-white/65 px-2 py-[2px] text-[11px] font-medium leading-none text-[#2C2C2C] whitespace-nowrap lowercase shrink-0">
                        {isVaultGridAdmin
                          ? t("chats.roles.vaultgrid_admin")
                          : isSystem
                            ? t("chats.roles.system")
                            : t("chats.roles.host")}
                      </span>
                    )}
                  </div>
                  {canToggleTranslation ? (
                    <button
                      type="button"
                      onClick={toggleTranslation}
                      disabled={translationLoading}
                      className={`${bubbleMutedClass} text-[15px] font-light underline underline-offset-2 shrink-0 disabled:opacity-60`}
                    >
                      {translateButtonLabel}
                    </button>
                  ) : null}
                </div>
              </>
            )}

            {renderReplyPreview()}
            {renderImages()}
            {renderChatLocation()}
            {renderChatPoll()}
            {renderConceptPoll()}
            {renderTextContent()}
            {renderGalleryUploadPreview()}
            {renderBubbleMeta()}
          </div>
        </div>
        {facilitatorButtonsNode ? (
          <div
            className={`mt-2 flex ${hideSenderBadge ? "justify-start" : "pl-11 justify-start"}`}
          >
            <div className="w-full max-w-[82%] flex justify-center">
              {facilitatorButtonsNode}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default function ChatFeed({
  items = [],
  onRequestMenu,
  onToggleReaction,
  onOpenImage,
  contextSelectedId = null,
  flashId = null,
  onJumpTo,
  avatarByCode = {},
  onOpenProfileByCode,
  hideSenderBadge = false,
  variant = "event",
  mentionContext = null,
  currentUserId = null,
  allowSystemMessageActions = false,
  selectionMode = false,
  selectedMessageId = null,
  canSelectMessage = null,
  onSelectMessage = null,
  onOpenLocation = null,
  onVotePoll = null,
  directInlineReportMessageId = null,
  onDirectInlineReportClick = null,
  directInlineReportLabel = "",
  directInlineReportAria = "",
  directInlineReportDisabled = false,
  onDirectPhotoConsentAction = null,
  directPhotoConsentSubmitting = false,
  onOpenHostRules = null,
}) {
  const { i18n, t } = useTranslation("menu");
  const isDesktop = useIsDesktop();
  const allowedMentionSet = null;
  const normalizedDirectInlineReportMessageId = Number(directInlineReportMessageId || 0);
  const requestSpecialItemMenu = (event, item) => {
    if (!allowSystemMessageActions || !item?.id || typeof onRequestMenu !== "function") return;
    if (event.target?.closest?.("a, button")) return;
    event.preventDefault();
    event.stopPropagation();
    onRequestMenu({ item, anchorEl: event.currentTarget, rowEl: event.currentTarget });
  };

  const handleSystemUsernameClick = useCallback(
    async (rawUsername) => {
      if (!onOpenProfileByCode) return;
      const username = String(rawUsername || "");
      if (!username) return;
      const normalized = normalizeMentionUsername(username);
      if (!normalized) return;

      try {
        const params = { username };
        const eventId = mentionContext?.eventId;
        const directChatId = mentionContext?.directChatId;
        if (eventId != null) params.event_id = eventId;
        if (directChatId != null) params.direct_chat_id = directChatId;

        const resp = await api.get("/chats/resolve-username/", {
          params,
        });
        const code = resp?.data?.code;
        if (code && !isSupportCode(code)) onOpenProfileByCode(code);
      } catch (e) {
        const status = e?.response?.status;
        const backendMsg = String(e?.response?.data?.error || "").trim();
        const mentionNotFound =
          status === 404 ||
          backendMsg === "USER_NOT_FOUND" ||
          backendMsg === "STORAGE_NOT_FOUND";

        const msg = mentionNotFound
          ? t("chats.mentions.user_not_found")
          : backendMsg || t("chats.mentions.resolve_failed");

        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message: msg, status: status || 400 },
          })
        );
      }
    },
    [onOpenProfileByCode, mentionContext, t]
  );

  const itemsWithDays = useMemo(() => {
    const out = [];
    let lastDayKey = null;

    for (const it of items || []) {
      // If backend already provides separators/system blocks, keep as-is
      if (it?.type === ITEM_TYPES.DAY || it?.type === ITEM_TYPES.SYSTEM) {
        out.push(it);
        continue;
      }

      const createdAt = it?.created_at;
      if (createdAt) {
        const d = new Date(createdAt);
        if (!Number.isNaN(d.getTime())) {
          const dayKey = d.toISOString().slice(0, 10); // YYYY-MM-DD
          if (dayKey !== lastDayKey) {
            out.push({ type: ITEM_TYPES.DAY, date: createdAt });
            lastDayKey = dayKey;
          }
        }
      }

      out.push(it);
    }

    return out;
  }, [items]);

  return (
    <div className="flex flex-col">
      {itemsWithDays.map((item, idx) => {
        if (item?.type === ITEM_TYPES.DAY) {
          return (
            <DaySeparator
              key={`day-${String(item.date || "").slice(0, 10) || idx}`}
              date={item.date}
              locale={i18n.language}
            />
          );
        }
        if (item?.type === ITEM_TYPES.SYSTEM) {
          return (
            <SystemMessage
              key={`sys-${item?.id ?? `${String(item?.text || "")}-${idx}`}`}
              lines={item.lines || []}
              payload={item.payload || null}
              locale={i18n.language}
              onUsernameClick={handleSystemUsernameClick}
              allowedMentionSet={allowedMentionSet}
            />
          );
        }
        const eventHostContactPayload = extractEventHostContactPayload(item?.payload);
        const directPhotoConsentPayload = getDirectPhotoConsentPayload(item?.payload);
        if (variant === "direct" && directPhotoConsentPayload) {
          return (
            <div
              key={item?.id ?? `photo-consent-${idx}`}
              role={allowSystemMessageActions ? "button" : undefined}
              tabIndex={allowSystemMessageActions ? 0 : undefined}
              onClick={allowSystemMessageActions ? (event) => requestSpecialItemMenu(event, item) : undefined}
              onContextMenu={allowSystemMessageActions ? (event) => requestSpecialItemMenu(event, item) : undefined}
              onKeyDown={
                allowSystemMessageActions
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        requestSpecialItemMenu(event, item);
                      }
                    }
                  : undefined
              }
            >
              <DirectPhotoConsentMessage
                item={item}
                payload={directPhotoConsentPayload}
                currentUserId={currentUserId}
                submitting={directPhotoConsentSubmitting}
                onAction={onDirectPhotoConsentAction}
                t={t}
              />
            </div>
          );
        }
        if (
          item?.type === ITEM_TYPES.MESSAGE &&
          String(item?.message_type || "").trim().toLowerCase() === ITEM_TYPES.SYSTEM_AS_USER &&
          eventHostContactPayload
        ) {
          return (
            <div
              key={item?.id ?? `msg-${idx}`}
              data-msg-id={item?.id}
              className="my-3 flex justify-center"
              role={allowSystemMessageActions ? "button" : undefined}
              tabIndex={allowSystemMessageActions ? 0 : undefined}
              onClick={allowSystemMessageActions ? (event) => requestSpecialItemMenu(event, item) : undefined}
              onContextMenu={allowSystemMessageActions ? (event) => requestSpecialItemMenu(event, item) : undefined}
              onKeyDown={
                allowSystemMessageActions
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        requestSpecialItemMenu(event, item);
                      }
                    }
                  : undefined
              }
            >
              <div className={`w-full ${isDesktop ? "max-w-[520px]" : "max-w-[360px]"}`}>
                <DirectChatEventContextNotice
                  event={eventHostContactPayload.event}
                  title={item?.text || ""}
                />
              </div>
            </div>
          );
        }
        const itemKey = item?.client_id || item?.id || `msg-${idx}`;
        const messageNode = (
          <MessageBubble
            item={{ ...item, onSystemUsernameClick: handleSystemUsernameClick }}
            onRequestMenu={onRequestMenu}
            onToggleReaction={onToggleReaction}
            onOpenImage={onOpenImage}
            isContextHidden={!!contextSelectedId && contextSelectedId === item?.id}
            flashId={flashId}
            onJumpTo={onJumpTo}
            avatarSrc={item?.avatarSrc || avatarByCode?.[item?.sender_code] || null}
            onOpenProfileByCode={onOpenProfileByCode}
            allowedMentionSet={allowedMentionSet}
            pollEventId={mentionContext?.eventId || null}
            currentUserId={currentUserId}
            // Скрываем бейдж, если явно попросили ИЛИ если это десктопный direct-чат
            hideSenderBadge={hideSenderBadge || (isDesktop && variant === "direct")}
            variant={variant}
            selectionMode={selectionMode}
            isSelectableMessage={
              typeof canSelectMessage === "function" ? !!canSelectMessage(item) : false
            }
            isSelectedMessage={Number(selectedMessageId || 0) === Number(item?.id || 0)}
            onSelectMessage={onSelectMessage}
            onOpenLocation={onOpenLocation}
            onVotePoll={onVotePoll}
            onOpenHostRules={onOpenHostRules}
          />
        );
        const showDirectInlineReport =
          variant === "direct" &&
          normalizedDirectInlineReportMessageId > 0 &&
          Number(item?.id || 0) === normalizedDirectInlineReportMessageId &&
          !item?.is_self &&
          typeof onDirectInlineReportClick === "function";

        if (!showDirectInlineReport) {
          if (!item?.local_echo) {
            return <React.Fragment key={itemKey}>{messageNode}</React.Fragment>;
          }
          return (
            <div
              key={itemKey}
              className="vg-chat-message-local-entry"
              data-delivery-state={item?.delivery_status || "sent"}
            >
              {messageNode}
            </div>
          );
        }

        return (
          <React.Fragment key={itemKey}>
            {messageNode}
            <div className="-mt-1 mb-4 flex w-full justify-center">
              <button
                type="button"
                disabled={directInlineReportDisabled}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDirectInlineReportClick?.(item);
                }}
                className="inline-flex min-h-9 max-w-[82%] min-w-0 items-center justify-center rounded-xl border border-white/25 bg-white/20 px-4 py-2 text-[12px] font-semibold leading-none text-white transition-colors hover:bg-white/30 active:scale-[0.98] disabled:cursor-default disabled:opacity-45"
                aria-label={
                  directInlineReportAria ||
                  t("chats.direct_chat.inline_report_aria", {
                    defaultValue: "Report this message",
                  })
                }
              >
                <span className="truncate">
                  {directInlineReportLabel ||
                    t("chats.direct_chat.inline_report_button", {
                      defaultValue: "Report message",
                    })}
                </span>
              </button>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
